import { useState, useEffect, useRef, useCallback } from "react";
import { authAPI, usersAPI, applicationsAPI, ticketsAPI, statsAPI, systemConfigAPI, fileUrl, bidsAPI, questionsAPI, contractorsAPI, tempContractorsAPI, clientsAPI, propertiesAPI, mapsAPI, importAPI } from "./api";

const WORK_TYPES = ["Locksmithing","Electrical","Plumbing","Heating","Renovation","Cleanout","Surrender","Investigate","Other"];
const URGENCY    = ["Emergency","High","Medium","Low"];
const ROLE_LABELS = { user:"Standard User", contractor:"Contractor/Vendor", contractor_pending:"Contractor (Pending)", ap_ar:"AP/AR", admin:"Admin", superadmin:"Super Admin" };
const STATUS_TABS = ["New","Open","Assigned to Contractor","Work Scheduled","Work Complete","Payment Issued","Closed/Completed","Cancelled"];
const STATUS_COLORS = { "New":"#3b82f6","Open":"#f59e0b","Assigned to Contractor":"#f97316","Work Scheduled":"#a855f7","Work Complete":"#8b5cf6","Payment Issued":"#10b981","Closed/Completed":"#6b7280","Cancelled":"#ef4444" };
const URGENCY_COLORS = { Emergency:"#ef4444",High:"#f97316",Medium:"#eab308",Low:"#22c55e" };
const PROPERTY_STATUSES = ["Needs 1st Visit","Seen","Needs Work","Active/Occupied","Vacant","Under Contract","On-Market","Good","Sold","Surrendered","Closed"];
const ACTIVE_PROPERTY_STATUSES = ["Needs 1st Visit","Seen","Needs Work","Active/Occupied","Vacant","Under Contract","On-Market","Good"];
const PROPERTY_STATUS_COLORS = {
  "Needs 1st Visit":"#ef4444",
  "Seen":"#22c55e",
  "Needs Work":"#f97316",
  "Active/Occupied":"#3b82f6",
  "Vacant":"#f59e0b",
  "Under Contract":"#a855f7",
  "On-Market":"#06b6d4",
  "Good":"#14b8a6",
  "Sold":"#6b7280","Surrendered":"#6b7280","Closed":"#6b7280",
};
const KEY_STATUSES = ["Keys Available","Locksmith Required"];
const PROPERTY_TYPES = ["Single-Family","Multi-Family","Condo/Co-op","Rental Apartment","Commercial","Mobile Home","Storage Unit","Land/Vacant Lot"];
const LICENSED_TRADES = ["Electrical","Plumbing","HVAC / Heating & Cooling","General Contracting","Locksmithing"];
const NY_JURISDICTIONS = ["New York City (all 5 boroughs)","Nassau County","Suffolk County","Rockland County","Westchester County"];
const ALL_SERVICES = ["Locksmithing","Electrical","Plumbing","HVAC / Heating & Cooling","General Contracting","Carpentry / Woodworking","Masonry / Concrete","Roofing","Flooring","Painting / Drywall","Welding / Metalwork","Fire Suppression / Sprinkler Systems","Security Systems / Cameras","Solar Panel Installation","Elevator / Lift Service","Pest Control","Landscaping / Groundskeeping","Snow Removal","Demolition","Hazmat / Asbestos Removal","Mold Remediation","Cleaning / Janitorial","Moving Services","Appliance Repair","Window / Glass Repair","Door / Hardware Installation"];

// ── Themes ────────────────────────────────────────────────────────────────────
const THEME_ALLIED = {
  bg:"#0f1117", surface:"#181c27", border:"#252b3b",
  accent:"#f5a623", accentDim:"#7a5210",
  text:"#e8eaf0", muted:"#6b7280",
  danger:"#ef4444", success:"#10b981", radius:"6px",
  name:"allied",
};
const THEME_CONTRACTOR = {
  bg:"#1e2128", surface:"#252b3b", border:"#333a4d",
  accent:"#f5a623", accentDim:"#7a5210",
  text:"#e8eaf0", muted:"#6b7280",
  danger:"#ef4444", success:"#10b981", radius:"6px",
  name:"contractor",
};

// ── Utilities ─────────────────────────────────────────────────────────────────
function formatPhone(val) {
  const digits = (val||"").replace(/\D/g,"").slice(0,10);
  if (digits.length===0) return "";
  if (digits.length<=3) return `(${digits}`;
  if (digits.length<=6) return `(${digits.slice(0,3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
}

function getTheme(user, portalHint) {
  if (user) {
    if (user.role === "contractor" || user.role === "contractor_pending") return THEME_CONTRACTOR;
    return THEME_ALLIED;
  }
  return portalHint === "contractor" ? THEME_CONTRACTOR : THEME_ALLIED;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

function makeCss(G) { return `
  @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;800&family=IBM+Plex+Mono:wght@400;500&family=Barlow:wght@400;500;600&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{background:${G.bg};color:${G.text};font-family:'Barlow',sans-serif;min-height:100vh}
  ::-webkit-scrollbar{width:6px;height:6px}
  ::-webkit-scrollbar-track{background:${G.surface}}
  ::-webkit-scrollbar-thumb{background:${G.accentDim};border-radius:3px}
  input,select,textarea{background:${G.bg};border:1px solid ${G.border};color:${G.text};border-radius:${G.radius};padding:9px 12px;font-family:'Barlow',sans-serif;font-size:14px;width:100%;outline:none;transition:border-color .2s}
  input:focus,select:focus,textarea:focus{border-color:${G.accent}}
  input[type=checkbox],input[type=radio]{width:auto;accent-color:${G.accent};cursor:pointer}
  label{font-size:12px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:${G.muted};display:block;margin-bottom:5px}
  button{cursor:pointer;border:none;border-radius:${G.radius};font-family:'Barlow',sans-serif;font-weight:600;font-size:14px;transition:all .18s}
  select option{background:${G.surface};color:${G.text}}
  .mono{font-family:'IBM Plex Mono',monospace}
  .page-enter{animation:fadeIn .25s ease}
  @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
  .card-hover:hover{border-color:${G.accent}!important}
  @keyframes spin{to{transform:rotate(360deg)}}
  @media(max-width:768px){
    .mobile-stack{flex-direction:column!important}
    .mobile-full{width:100%!important}
    .mobile-hide{display:none!important}
    .mobile-pad{padding:16px!important}
    .mobile-text-sm{font-size:12px!important}
    .grid-2,.grid-3,.grid-4{grid-template-columns:1fr!important}
    .grid-2-1{grid-template-columns:1fr!important}
    .grid-3-1-1{grid-template-columns:1fr!important}
  }
`; }

function fmtDate(iso) { return iso?new Date(iso).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}):"-"; }

function Btn({ children, onClick, variant="primary", size="md", disabled, loading, style={}, G }) {
  const variants = {
    primary:{background:G.accent,color:G.name==="contractor"?"#fff":"#000"},
    secondary:{background:G.surface,color:G.text,border:`1px solid ${G.border}`},
    ghost:{background:"transparent",color:G.muted,border:`1px solid ${G.border}`},
    danger:{background:G.danger,color:"#fff"},
    success:{background:G.success,color:"#fff"},
  };
  const sizes = { sm:{padding:"6px 14px",fontSize:"12px"},md:{padding:"9px 20px"},lg:{padding:"13px 28px",fontSize:"16px"} };
  return <button onClick={onClick} disabled={disabled||loading} style={{...variants[variant],...sizes[size],opacity:(disabled||loading)?.6:1,...style}}>{loading?"...":children}</button>;
}
function Field({ label, children, required, hint, G }) {
  return <div style={{marginBottom:16}}><label style={{color:G.muted}}>{label}{required&&<span style={{color:G.danger}}> *</span>}</label>{children}{hint&&<div style={{fontSize:11,color:G.muted,marginTop:4}}>{hint}</div>}</div>;
}
function Badge({ text, color }) {
  return <span style={{background:color+"22",color,border:`1px solid ${color}44`,borderRadius:"4px",padding:"2px 8px",fontSize:"11px",fontWeight:600,letterSpacing:".06em",textTransform:"uppercase",whiteSpace:"nowrap"}}>{text}</span>;
}
function Modal({ title, children, onClose, width=580, noConfirm=false, G }) {
  const [confirmCancel, setConfirmCancel] = useState(false);
  function handleClose() { if (noConfirm) { onClose(); } else { setConfirmCancel(true); } }
  function handleConfirmCancel() { setConfirmCancel(false); onClose(); }
  function handleKeepEditing() { setConfirmCancel(false); }
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.8)",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,width:"100%",maxWidth:width,maxHeight:"92vh",overflow:"auto",display:"flex",flexDirection:"column"}} className="page-enter">
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"18px 24px",borderBottom:`1px solid ${G.border}`,flexShrink:0}}>
          <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:20,letterSpacing:".04em",color:G.text}}>{title}</span>
          <button onClick={handleClose} style={{background:"none",color:G.muted,fontSize:22,lineHeight:1,cursor:"pointer",border:"none",padding:"0 4px"}}>×</button>
        </div>
        <div style={{padding:24,overflowY:"auto"}}>{children}</div>
      </div>
      {confirmCancel&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.6)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
          <div style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,padding:28,maxWidth:400,width:"100%",textAlign:"center"}} className="page-enter">
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:20,color:G.text,marginBottom:12}}>Discard Changes?</div>
            <div style={{color:G.muted,fontSize:14,marginBottom:24}}>Are you sure you want to cancel? Any data already entered will not be saved.</div>
            <div style={{display:"flex",gap:10,justifyContent:"center"}}>
              <Btn variant="danger" onClick={handleConfirmCancel} G={G}>Yes, Discard</Btn>
              <Btn variant="secondary" onClick={handleKeepEditing} G={G}>Keep Editing</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function Alert({ msg, type="error", G }) {
  if (!msg) return null;
  const c = type==="error"?G.danger:G.success;
  return <div style={{background:c+"18",border:`1px solid ${c}44`,color:c,borderRadius:G.radius,padding:"10px 14px",fontSize:13,marginBottom:16}}>{msg}</div>;
}
function Spinner({ G }) { return <div style={{width:28,height:28,border:`3px solid ${G.border}`,borderTopColor:G.accent,borderRadius:"50%",animation:"spin .7s linear infinite",margin:"40px auto"}} />; }

// ── Auth Page ─────────────────────────────────────────────────────────────────
function AuthPage({ onLogin }) {
  const [portal, setPortal] = useState("allied");
  const [step, setStep] = useState("login");
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [verifyToken, setVerifyToken] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const G = getTheme(null, portal);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (token) {
      authAPI.verifyEmailToken(token)
        .then(r => { setVerifiedEmail(r.email); setVerifyToken(token); setPortal(r.portalType||"allied"); setStep("set-password"); window.history.replaceState({},"",window.location.pathname); })
        .catch(() => setErr("This verification link is invalid or has expired. Please request a new one."));
    }
  }, []);

  function switchPortal(p) { setPortal(p); setStep("login"); setErr(""); }

  return (
    <>
      <style>{makeCss(G)}</style>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",padding:16,background:G.bg}}>
        <div style={{width:"100%",maxWidth:460}}>
          <div style={{textAlign:"center",marginBottom:32}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:48,letterSpacing:".06em",lineHeight:1}}>
              <span style={{color:G.name==="contractor"?G.accentDim:G.accent}}>PROP</span><span style={{color:G.text}}>TRACK</span>
            </div>
            <div style={{color:G.muted,fontSize:12,letterSpacing:".14em",marginTop:6}}>PROPERTY MAINTENANCE PORTAL</div>
          </div>
          <div style={{display:"flex",background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,padding:4,marginBottom:24,gap:4}}>
            {[["allied","Allied CSS"],["contractor","Contractor / Vendor"]].map(([p,label])=>(
              <button key={p} onClick={()=>switchPortal(p)} style={{flex:1,padding:"10px",borderRadius:6,border:"none",background:portal===p?G.accent+"33":"transparent",color:portal===p?G.accent:G.muted,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:".06em",cursor:"pointer",boxShadow:portal===p?`inset 0 0 0 1px ${G.accent}`:"none"}}>{label}</button>
            ))}
          </div>
          <div style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,padding:28}}>
            <Alert msg={err} G={G} />
            {step==="login"        && <LoginForm portal={portal} G={G} onLogin={onLogin} onRegister={()=>setStep("initiate")} setErr={setErr} loading={loading} setLoading={setLoading} />}
            {step==="initiate"     && <InitiateEmailForm portal={portal} G={G} onSent={()=>setStep("check-email")} onBack={()=>setStep("login")} setErr={setErr} loading={loading} setLoading={setLoading} />}
            {step==="check-email"  && <CheckEmailScreen portal={portal} G={G} onBack={()=>setStep("initiate")} />}
            {step==="set-password" && portal==="allied"     && <AlliedSetPasswordForm token={verifyToken} email={verifiedEmail} G={G} onLogin={onLogin} setErr={setErr} loading={loading} setLoading={setLoading} />}
            {step==="set-password" && portal==="contractor" && <ContractorSetPasswordForm token={verifyToken} email={verifiedEmail} G={G} onNext={(user,tok)=>{localStorage.setItem("proptrack_token",tok);onLogin(user);}} setErr={setErr} loading={loading} setLoading={setLoading} />}
          </div>
        </div>
      </div>
    </>
  );
}

function LoginForm({ portal, G, onLogin, onRegister, setErr, loading, setLoading }) {
  const [form, setForm] = useState({ email:"", password:"" });
  async function handleSubmit(e) {
    e.preventDefault(); setErr(""); setLoading(true);
    try { const res = await authAPI.login({ email:form.email, password:form.password }); localStorage.setItem("proptrack_token",res.token); onLogin(res.user); }
    catch(ex) { setErr(ex.message); }
    finally { setLoading(false); }
  }
  return (
    <form onSubmit={handleSubmit}>
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:22,marginBottom:20,color:G.accent,letterSpacing:".04em"}}>{portal==="allied"?"Allied CSS Staff Sign In":"Contractor Sign In"}</div>
      <Field label="Email" required G={G}><input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} placeholder={portal==="allied"?"your@alliedcss.org":"your@email.com"} autoComplete="email" /></Field>
      <Field label="Password" required G={G}><input type="password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} placeholder="Enter password" autoComplete="current-password" /></Field>
      <Btn style={{width:"100%",marginTop:8}} size="lg" loading={loading} G={G}>Sign In</Btn>
      <div style={{textAlign:"center",marginTop:16}}>
        <span style={{color:G.muted,fontSize:13}}>{portal==="allied"?"New Allied CSS staff? ":"New contractor? "}</span>
        <button type="button" onClick={onRegister} style={{background:"none",border:"none",color:G.accent,fontSize:13,fontWeight:600,cursor:"pointer"}}>{portal==="allied"?"Create account":"Apply as contractor"}</button>
      </div>
    </form>
  );
}

function InitiateEmailForm({ portal, G, onSent, onBack, setErr, loading, setLoading }) {
  const [email, setEmail] = useState("");
  async function handleSubmit(e) {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await authAPI.initiateEmail(email, portal); onSent(); }
    catch(ex) { setErr(ex.message); }
    finally { setLoading(false); }
  }
  return (
    <form onSubmit={handleSubmit}>
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:22,marginBottom:8,color:G.accent,letterSpacing:".04em"}}>{portal==="allied"?"Create Allied CSS Account":"Apply as Contractor"}</div>
      <p style={{color:G.muted,fontSize:13,marginBottom:20}}>{portal==="allied"?"Enter your @alliedcss.org email. We'll send a verification link.":"Enter your business email to begin your contractor application."}</p>
      <Field label="Email Address" required G={G}><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder={portal==="allied"?"you@alliedcss.org":"you@yourbusiness.com"} autoComplete="email" /></Field>
      <div style={{display:"flex",gap:10,marginTop:8}}><Btn style={{flex:1}} size="lg" loading={loading} G={G}>Send Verification Email</Btn><Btn variant="ghost" onClick={onBack} G={G}>Back</Btn></div>
    </form>
  );
}

function CheckEmailScreen({ portal, G, onBack }) {
  return (
    <div style={{textAlign:"center",padding:"8px 0"}}>
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:24,fontWeight:700,marginBottom:8,color:G.text}}>Check Your Email</div>
      <p style={{color:G.muted,fontSize:14,marginBottom:24,lineHeight:1.6}}>We have sent a verification link to your email address.<br/>Click the link to continue {portal==="contractor"?"your application":"creating your account"}.<br/><span style={{fontSize:12}}>The link expires in 24 hours.</span></p>
      <Btn variant="ghost" onClick={onBack} G={G}>Use a different email</Btn>
    </div>
  );
}

function AlliedSetPasswordForm({ token, email, G, onLogin, setErr, loading, setLoading }) {
  const [form, setForm] = useState({ name:"", password:"", confirm:"", role:"user" });
  async function handleSubmit(e) {
    e.preventDefault(); setErr("");
    if (form.password!==form.confirm) return setErr("Passwords do not match");
    setLoading(true);
    try { const res=await authAPI.completeRegistration({token,name:form.name,password:form.password,role:form.role}); localStorage.setItem("proptrack_token",res.token); onLogin(res.user); }
    catch(ex) { setErr(ex.message); }
    finally { setLoading(false); }
  }
  return (
    <form onSubmit={handleSubmit}>
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:22,marginBottom:8,color:G.accent,letterSpacing:".04em"}}>Complete Your Account</div>
      <p style={{color:G.muted,fontSize:13,marginBottom:20}}>Email verified: <strong style={{color:G.text}}>{email}</strong></p>
      <Field label="Full Name" required G={G}><input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Your full name" /></Field>
      <Field label="Account Type" G={G}><select value={form.role} onChange={e=>setForm({...form,role:e.target.value})}><option value="user">Standard User</option><option value="ap_ar">AP / AR</option></select></Field>
      <Field label="Password" required G={G}><input type="password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} placeholder="Choose a password (min 6 characters)" autoComplete="new-password" /></Field>
      <Field label="Confirm Password" required G={G}><input type="password" value={form.confirm} onChange={e=>setForm({...form,confirm:e.target.value})} placeholder="Repeat your password" autoComplete="new-password" /></Field>
      <Btn style={{width:"100%",marginTop:8}} size="lg" loading={loading} G={G}>Create Account</Btn>
    </form>
  );
}

function ContractorSetPasswordForm({ token, email, G, onNext, setErr, loading, setLoading }) {
  const [form, setForm] = useState({ name:"", password:"", confirm:"" });
  async function handleSubmit(e) {
    e.preventDefault(); setErr("");
    if (form.password!==form.confirm) return setErr("Passwords do not match");
    setLoading(true);
    try { const res=await authAPI.contractorInitiate({token,name:form.name,password:form.password}); onNext(res.user,res.token); }
    catch(ex) { setErr(ex.message); }
    finally { setLoading(false); }
  }
  return (
    <form onSubmit={handleSubmit}>
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:22,marginBottom:8,color:G.accent,letterSpacing:".04em"}}>Set Up Your Account</div>
      <p style={{color:G.muted,fontSize:13,marginBottom:20}}>Email verified: <strong style={{color:G.text}}>{email}</strong><br/><span style={{fontSize:12}}>You will complete your application after setting up your account.</span></p>
      <Field label="Full Name" required G={G}><input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="Your full name" /></Field>
      <Field label="Password" required G={G}><input type="password" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} placeholder="Choose a password" autoComplete="new-password" /></Field>
      <Field label="Confirm Password" required G={G}><input type="password" value={form.confirm} onChange={e=>setForm({...form,confirm:e.target.value})} placeholder="Repeat your password" autoComplete="new-password" /></Field>
      <Btn style={{width:"100%",marginTop:8}} size="lg" loading={loading} G={G}>Continue to Application</Btn>
    </form>
  );
}

// ── Contractor Application ────────────────────────────────────────────────────
function ContractorApplicationPage({ existingApp, onSubmitted, G }) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState(existingApp ? {
    companyName:existingApp.companyName||"", phone:existingApp.phone||"",
    billingStreet:existingApp.billingStreet||"", billingUnit:existingApp.billingUnit||"",
    billingCity:existingApp.billingCity||"", billingState:existingApp.billingState||"NY", billingZip:existingApp.billingZip||"",
    services:existingApp.services||[], licenses:existingApp.licenses||[],
  } : { companyName:"",phone:"",billingStreet:"",billingUnit:"",billingCity:"",billingState:"NY",billingZip:"",services:[],licenses:[] });
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false); const [success, setSuccess] = useState(false);

  function toggleService(s) { setForm(f=>({...f,services:f.services.includes(s)?f.services.filter(x=>x!==s):[...f.services,s]})); }
  function setLicense(trade,field,value) {
    setForm(f=>{ const ex=f.licenses.find(l=>l.trade===trade)||{trade,licensed:false,jurisdictions:[]}; const upd={...ex,[field]:value}; if(field==="licensed"&&!value) upd.jurisdictions=[]; return {...f,licenses:[...f.licenses.filter(l=>l.trade!==trade),upd]}; });
  }
  function getLicense(trade) { return form.licenses.find(l=>l.trade===trade)||{trade,licensed:false,jurisdictions:[]}; }
  function toggleJurisdiction(trade,j) { const lic=getLicense(trade); setLicense(trade,"jurisdictions",lic.jurisdictions.includes(j)?lic.jurisdictions.filter(x=>x!==j):[...lic.jurisdictions,j]); }

  async function handleSubmit(e) {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await applicationsAPI.submit(form); setSuccess(true); onSubmitted(); }
    catch(ex) { setErr(ex.message); }
    finally { setLoading(false); }
  }

  const licensedSelected = form.services.filter(s=>LICENSED_TRADES.includes(s));
  const accentBlue = G.name==="contractor" ? G.accentDim : "#60a5fa";

  if (success) return (
    <div style={{maxWidth:560,margin:"60px auto",textAlign:"center",padding:24}} className="page-enter">
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,fontWeight:700,marginBottom:8,color:G.text}}>Application Submitted!</div>
      <p style={{color:G.muted,lineHeight:1.6}}>Thank you for applying to become an Allied CSS contractor. Our team will review your application and be in touch soon.</p>
    </div>
  );

  return (
    <div style={{maxWidth:760,margin:"0 auto",padding:isMobile?"12px":"32px 16px"}} className="page-enter">
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:isMobile?24:34,letterSpacing:".04em",marginBottom:8,color:G.text}}><span style={{color:accentBlue}}>//</span> CONTRACTOR APPLICATION</div>
      <p style={{color:G.muted,marginBottom:24,fontSize:14}}>Complete your application to become an Allied CSS approved contractor.</p>
      {existingApp?.status==="needs_info"&&existingApp?.adminNotes&&(
        <div style={{background:G.danger+"18",border:`1px solid ${G.danger}44`,borderRadius:G.radius,padding:"14px 18px",marginBottom:24}}>
          <div style={{color:G.danger,fontWeight:700,marginBottom:6}}>Additional Information Required</div>
          <div style={{color:G.text,fontSize:13}}>{existingApp.adminNotes}</div>
        </div>
      )}
      {existingApp?.status==="approved_needs_info"&&existingApp?.adminNotes&&(
        <div style={{background:G.accent+"18",border:`1px solid ${G.accent}44`,borderRadius:G.radius,padding:"14px 18px",marginBottom:24}}>
          <div style={{color:G.accent,fontWeight:700,marginBottom:6}}>Please Update Your Profile</div>
          <div style={{color:G.text,fontSize:13}}>{existingApp.adminNotes}</div>
        </div>
      )}
      <Alert msg={err} G={G} />
      <form onSubmit={handleSubmit}>
        <div style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,padding:20,marginBottom:20}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:18,marginBottom:16,color:accentBlue}}>Company Information</div>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:14}}>
            <Field label="Company Name" required G={G}><input value={form.companyName} onChange={e=>setForm({...form,companyName:e.target.value})} placeholder="Your company name" /></Field>
            <Field label="Phone Number" required G={G}><input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} placeholder="(555) 555-5555" /></Field>
          </div>
        </div>
        <div style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,padding:20,marginBottom:20}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:18,marginBottom:16,color:accentBlue}}>Billing Address</div>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"2fr 1fr",gap:14}}>
            <Field label="Street Address" required G={G}><input value={form.billingStreet} onChange={e=>setForm({...form,billingStreet:e.target.value})} placeholder="123 Main Street" /></Field>
            <Field label="Unit / Suite" G={G}><input value={form.billingUnit} onChange={e=>setForm({...form,billingUnit:e.target.value})} placeholder="Apt 4B" /></Field>
          </div>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"2fr 1fr 1fr",gap:14}}>
            <Field label="City" required G={G}><input value={form.billingCity} onChange={e=>setForm({...form,billingCity:e.target.value})} placeholder="New York" /></Field>
            <Field label="State" required G={G}><input value={form.billingState} onChange={e=>setForm({...form,billingState:e.target.value})} placeholder="NY" /></Field>
            <Field label="Zip Code" required G={G}><input value={form.billingZip} onChange={e=>setForm({...form,billingZip:e.target.value})} placeholder="10001" /></Field>
          </div>
        </div>
        <div style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,padding:20,marginBottom:20}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:18,marginBottom:4,color:accentBlue}}>Services Offered</div>
          <p style={{color:G.muted,fontSize:13,marginBottom:16}}>Select all services your company provides.</p>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:8}}>
            {ALL_SERVICES.map(s=>(
              <label key={s} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:form.services.includes(s)?G.accent+"18":G.bg,border:`1px solid ${form.services.includes(s)?G.accent:G.border}`,borderRadius:G.radius,cursor:"pointer",textTransform:"none",fontSize:13,color:G.text,letterSpacing:"normal",fontWeight:form.services.includes(s)?600:400,margin:0}}>
                <input type="checkbox" checked={form.services.includes(s)} onChange={()=>toggleService(s)} style={{width:16,height:16,flexShrink:0}} />{s}
              </label>
            ))}
          </div>
        </div>
        {licensedSelected.length>0&&(
          <div style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,padding:20,marginBottom:20}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:18,marginBottom:4,color:accentBlue}}>Licenses</div>
            <p style={{color:G.muted,fontSize:13,marginBottom:16}}>For each licensed trade you selected, indicate whether you hold a license and which jurisdictions it covers.</p>
            {licensedSelected.map(trade=>{ const lic=getLicense(trade); return (
              <div key={trade} style={{marginBottom:16,padding:14,background:G.bg,borderRadius:G.radius,border:`1px solid ${G.border}`}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:lic.licensed?12:0}}>
                  <span style={{fontWeight:600,fontSize:14,color:G.text}}>{trade}</span>
                  <div style={{display:"flex",gap:16}}>
                    {[true,false].map(v=>(
                      <label key={String(v)} style={{display:"flex",alignItems:"center",gap:6,textTransform:"none",fontSize:13,color:G.text,letterSpacing:"normal",cursor:"pointer",margin:0}}>
                        <input type="radio" name={`lic-${trade}`} checked={lic.licensed===v} onChange={()=>setLicense(trade,"licensed",v)} />{v?"Licensed":"Not Licensed"}
                      </label>
                    ))}
                  </div>
                </div>
                {lic.licensed&&(
                  <div>
                    <div style={{fontSize:11,color:G.muted,marginBottom:8,textTransform:"uppercase",letterSpacing:".06em"}}>Current in which jurisdictions?</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                      {NY_JURISDICTIONS.map(j=>(
                        <label key={j} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 10px",background:lic.jurisdictions.includes(j)?accentBlue+"22":G.surface,border:`1px solid ${lic.jurisdictions.includes(j)?accentBlue:G.border}`,borderRadius:G.radius,cursor:"pointer",textTransform:"none",fontSize:12,color:G.text,letterSpacing:"normal",margin:0,fontWeight:lic.jurisdictions.includes(j)?600:400}}>
                          <input type="checkbox" checked={lic.jurisdictions.includes(j)} onChange={()=>toggleJurisdiction(trade,j)} style={{width:14,height:14}} />{j}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );})}
          </div>
        )}
        <Btn size="lg" style={{width:"100%"}} loading={loading} G={G}>{existingApp?"Update & Resubmit Application":"Submit Application for Review"}</Btn>
      </form>
    </div>
  );
}

// ── Nav ───────────────────────────────────────────────────────────────────────
function Nav({ user, page, setPage, onLogout, G }) {
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const isPending = user.role==="contractor_pending";
  const isContractorApproved = user.role==="contractor";
  const isAdmin = user.role==="admin"||user.role==="superadmin";
  const isAllied = ["user","ap_ar","admin","superadmin"].includes(user.role);
  const navItems = isPending?[]:[
    ...(isContractorApproved?[{id:"dashboard",label:"Dashboard"}]:[]),
    ...(isAllied?[{id:"properties",label:"Dashboard"},{id:"tickets",label:"Tickets"}]:[]),
    ...(isContractorApproved?[{id:"my-application",label:"My Profile"}]:[]),
    ...(isAllied?[{id:"clients",label:"Clients"}]:[]),
    ...(isAdmin?[{id:"admin",label:"Admin"}]:[]),
  ];

  function nav(id) { setPage(id); setMenuOpen(false); }

  return (
    <div style={{background:G.surface,borderBottom:`1px solid ${G.border}`,position:"sticky",top:0,zIndex:50}}>
      <div style={{padding:"0 16px",display:"flex",alignItems:"center",height:56,gap:8}}>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:24,letterSpacing:".06em",cursor:"pointer",marginRight:isMobile?0:16}} onClick={()=>!isPending&&nav("dashboard")}>
          <span style={{color:G.name==="contractor"?G.accentDim:G.accent}}>PROP</span><span style={{color:G.text}}>TRACK</span>
        </div>
        {!isMobile && (
          <div style={{display:"flex",gap:4,flex:1}}>
            {navItems.map(n=>(
              <button key={n.id} onClick={()=>nav(n.id)} style={{background:page===n.id?G.accent+"18":"transparent",border:`1px solid ${page===n.id?G.accent:G.border}`,color:page===n.id?G.accent:G.muted,borderRadius:G.radius,padding:"6px 16px",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:14,letterSpacing:".06em",textTransform:"uppercase",cursor:"pointer"}}>{n.label}</button>
            ))}
          </div>
        )}
        <div style={{flex:1}} />
        {!isMobile && (
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{textAlign:"right",cursor:"pointer"}} onClick={()=>nav("profile")}>
              <div style={{fontSize:13,fontWeight:600,color:page==="profile"?G.accent:G.text}}>{user.name}</div>
              <div style={{fontSize:11,color:G.accent,letterSpacing:".06em",textTransform:"uppercase"}}>{ROLE_LABELS[user.role]||user.role}</div>
            </div>
            <Btn variant="ghost" size="sm" onClick={onLogout} G={G}>Logout</Btn>
          </div>
        )}
        {isMobile && (
          <button onClick={()=>setMenuOpen(o=>!o)} style={{background:"none",border:`1px solid ${G.border}`,color:G.text,borderRadius:G.radius,padding:"6px 10px",fontSize:18,lineHeight:1,cursor:"pointer"}}>
            {menuOpen?"✕":"☰"}
          </button>
        )}
      </div>
      {isMobile && menuOpen && (
        <div style={{background:G.surface,borderTop:`1px solid ${G.border}`,padding:"8px 16px 16px"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",paddingBottom:12,marginBottom:8,borderBottom:`1px solid ${G.border}`}}>
            <div onClick={()=>nav("profile")} style={{cursor:"pointer"}}>
              <div style={{fontSize:13,fontWeight:600,color:G.text}}>{user.name}</div>
              <div style={{fontSize:11,color:G.accent,letterSpacing:".06em",textTransform:"uppercase"}}>{ROLE_LABELS[user.role]||user.role}</div>
            </div>
            <Btn variant="ghost" size="sm" onClick={onLogout} G={G}>Logout</Btn>
          </div>
          {navItems.map(n=>(
            <button key={n.id} onClick={()=>nav(n.id)} style={{display:"block",width:"100%",textAlign:"left",background:page===n.id?G.accent+"18":"transparent",border:`1px solid ${page===n.id?G.accent:"transparent"}`,color:page===n.id?G.accent:G.text,borderRadius:G.radius,padding:"10px 14px",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:".06em",textTransform:"uppercase",cursor:"pointer",marginBottom:4}}>{n.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Profile ───────────────────────────────────────────────────────────────────
function ProfilePage({ user, onUpdated, G }) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState({ name:user.name, email:user.email||"", currentPassword:"", newPassword:"", confirmPassword:"" });
  const [err, setErr] = useState(""); const [msg, setMsg] = useState(""); const [loading, setLoading] = useState(false);
  async function handleSubmit(e) {
    e.preventDefault(); setErr(""); setMsg("");
    if (!form.currentPassword) return setErr("Current password is required.");
    if (form.newPassword&&form.newPassword.length<6) return setErr("New password must be at least 6 characters.");
    if (form.newPassword!==form.confirmPassword) return setErr("New passwords do not match.");
    setLoading(true);
    try {
      const payload={name:form.name,email:form.email,currentPassword:form.currentPassword};
      if (form.newPassword) payload.newPassword=form.newPassword;
      const res=await authAPI.updateProfile(payload);
      localStorage.setItem("proptrack_token",res.token); onUpdated(res.user);
      setMsg("Profile updated successfully.");
      setForm(f=>({...f,currentPassword:"",newPassword:"",confirmPassword:""}));
    } catch(ex) { setErr(ex.message); }
    finally { setLoading(false); }
  }
  return (
    <div style={{maxWidth:520,margin:"0 auto",padding:isMobile?"12px":"32px 16px"}} className="page-enter">
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:isMobile?24:34,letterSpacing:".04em",marginBottom:28,color:G.text}}><span style={{color:G.accent}}>//</span> MY PROFILE</div>
      <div style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,padding:28}}>
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:24,paddingBottom:20,borderBottom:`1px solid ${G.border}`}}>
          <div style={{width:52,height:52,borderRadius:"50%",background:G.accent+"22",border:`2px solid ${G.accent}`,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:22,color:G.accent}}>{user.name.charAt(0).toUpperCase()}</div>
          <div><div style={{fontWeight:700,fontSize:16,color:G.text}}>{user.name}</div><div style={{fontSize:12,color:G.accent,textTransform:"uppercase",letterSpacing:".08em"}}>{ROLE_LABELS[user.role]||user.role}</div></div>
        </div>
        <Alert msg={err} G={G} /><Alert msg={msg} type="success" G={G} />
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom:20}}>
            <div style={{fontSize:12,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.accent,marginBottom:12}}>Account Info</div>
            <Field label="Display Name" required G={G}><input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></Field>
            <Field label="Email" G={G}><input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} /></Field>
          </div>
          <div style={{marginBottom:20,paddingTop:16,borderTop:`1px solid ${G.border}`}}>
            <div style={{fontSize:12,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.accent,marginBottom:12}}>Change Password</div>
            <Field label="New Password" G={G}><input type="password" value={form.newPassword} onChange={e=>setForm({...form,newPassword:e.target.value})} placeholder="Leave blank to keep current" autoComplete="new-password" /></Field>
            <Field label="Confirm New Password" G={G}><input type="password" value={form.confirmPassword} onChange={e=>setForm({...form,confirmPassword:e.target.value})} placeholder="Repeat new password" autoComplete="new-password" /></Field>
          </div>
          <div style={{paddingTop:16,borderTop:`1px solid ${G.border}`}}>
            <div style={{fontSize:12,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.accent,marginBottom:12}}>Confirm Changes</div>
            <Field label="Current Password" required G={G}><input type="password" value={form.currentPassword} onChange={e=>setForm({...form,currentPassword:e.target.value})} placeholder="Required to save any changes" autoComplete="current-password" /></Field>
          </div>
          <Btn size="lg" style={{width:"100%",marginTop:8}} loading={loading} G={G}>Save Changes</Btn>
        </form>
      </div>
    </div>
  );
}

// ── New Ticket ────────────────────────────────────────────────────────────────
function NewTicketPage({ onSaved, G }) {
  const isMobile = useIsMobile();
  const blank={propertyId:"",clientName:"",clientPhone:"",clientStreet:"",clientUnit:"",clientCity:"",clientState:"NY",clientZip:"",dateReported:new Date().toISOString().slice(0,10),workType:"Plumbing",description:"",urgency:"Medium",outsideContractor:false};
  const [form,setForm]=useState(blank); const [photoFile,setPhotoFile]=useState(null);
  const [err,setErr]=useState(""); const [loading,setLoading]=useState(false); const [success,setSuccess]=useState(false);
  const [propSearch,setPropSearch]=useState(""); const [propResults,setPropResults]=useState([]); const [propSearching,setPropSearching]=useState(false);
  const [selectedProp,setSelectedProp]=useState(null); const [showPropDropdown,setShowPropDropdown]=useState(false);
  // Client state
  const [clients,setClients]=useState([]); const [selectedClientId,setSelectedClientId]=useState(null);
  const [clientSearch,setClientSearch]=useState(""); const [showClientDropdown,setShowClientDropdown]=useState(false);
  const [addNewClient,setAddNewClient]=useState(false); const [newClientForm,setNewClientForm]=useState({firstName:"",lastName:"",notes:""});
  // Primary contact (when property selected)
  const [primaryContact,setPrimaryContact]=useState("");
  const [addUnlistedContact,setAddUnlistedContact]=useState(false);
  const [unlistedContact,setUnlistedContact]=useState({name:"",relationship:"",phone:"",email:""});
  const [savingUnlisted,setSavingUnlisted]=useState(false);
  // Manual address contacts
  const [contacts,setContacts]=useState([]);
  const [createNewProperty,setCreateNewProperty]=useState(false);
  const [newPropForm,setNewPropForm]=useState({propertyType:"Single-Family",status:"Needs Work",keyStatus:"Keys Available"});
  const [dupCandidates,setDupCandidates]=useState([]);
  const dupCheckTimer=useRef();
  // Client de-dup state (mirrors property duplicate detection)
  const [clientDupExact,setClientDupExact]=useState([]);
  const [clientDupFuzzy,setClientDupFuzzy]=useState([]);
  const clientDupTimer=useRef();

  // Debounced duplicate check on the new-client inline form
  useEffect(()=>{
    if (!addNewClient || (!newClientForm.firstName.trim() && !newClientForm.lastName.trim())) {
      setClientDupExact([]); setClientDupFuzzy([]); return;
    }
    clearTimeout(clientDupTimer.current);
    clientDupTimer.current=setTimeout(async()=>{
      try {
        const res=await clientsAPI.check({firstName:newClientForm.firstName, lastName:newClientForm.lastName});
        setClientDupExact(res.exact||[]);
        setClientDupFuzzy(res.fuzzy||[]);
      } catch{}
    },500);
  },[addNewClient, newClientForm.firstName, newClientForm.lastName]);

  // Debounced duplicate check when save-as-property is checked and street is filled
  useEffect(()=>{
    if (!createNewProperty||!form.clientStreet.trim()) { setDupCandidates([]); return; }
    clearTimeout(dupCheckTimer.current);
    dupCheckTimer.current=setTimeout(async()=>{
      try {
        const res=await propertiesAPI.list({search:form.clientStreet});
        // Fuzzy match: same street number + street name in same city
        const streetNum=(form.clientStreet.match(/^\d+/)||[""])[0];
        const matches=res.filter(p=>{
          if (form.clientCity&&p.city&&p.city.toLowerCase()!==form.clientCity.toLowerCase()) return false;
          if (streetNum&&!(p.street||"").startsWith(streetNum)) return false;
          return true;
        });
        setDupCandidates(matches.slice(0,3));
      } catch{}
    },500);
  },[createNewProperty,form.clientStreet,form.clientCity]);
  const photoRef=useRef(); const propSearchTimer=useRef();
  const submittingRef=useRef(false);

  function addContact() { setContacts(cs=>[...cs,{name:"",relationship:"",phone:"",email:""}]); }
  function removeContact(i) { setContacts(cs=>cs.filter((_,j)=>j!==i)); }
  function updateContact(i,field,val) { setContacts(cs=>cs.map((c,j)=>j===i?{...c,[field]:val}:c)); }

  useEffect(()=>{ clientsAPI.list().then(setClients).catch(()=>{}); },[]);

  // Typeahead search for properties
  function handlePropSearch(val) {
    setPropSearch(val); setShowPropDropdown(true);
    clearTimeout(propSearchTimer.current);
    if (!val.trim()) { setPropResults([]); setSelectedProp(null); return; }
    propSearchTimer.current = setTimeout(async()=>{
      setPropSearching(true);
      try { const res=await propertiesAPI.list({search:val}); setPropResults(res.slice(0,8)); }
      catch{} finally { setPropSearching(false); }
    }, 300);
  }

  function selectProperty(prop) {
    setSelectedProp(prop);
    setPropSearch([prop.street,prop.unit,prop.city,prop.state,prop.zip].filter(Boolean).join(", "));
    setShowPropDropdown(false);
    const clientName = prop.clientNames?.[0] || "";
    setForm(f=>({...f, propertyId:prop.id, clientName, clientStreet:prop.street||"", clientUnit:prop.unit||"", clientCity:prop.city||"", clientState:prop.state||"", clientZip:prop.zip||""}));
    // Auto-select client if property has exactly one linked client
    if (prop.clientIds?.length===1) {
      const c = clients.find(c=>c.id===prop.clientIds[0]);
      if (c) { setSelectedClientId(c.id); setClientSearch(c.name); setAddNewClient(false); }
      else if (prop.clientNames?.[0]) { setClientSearch(prop.clientNames[0]); } // fallback if clients not loaded yet
    } else if (prop.clientIds?.length>1) {
      setSelectedClientId(null); setClientSearch("");
    } else if (prop.clientNames?.[0]) {
      // No clientIds but name exists — show it as a hint
      setClientSearch(prop.clientNames[0]);
    }
  }

  function clearProperty() { setSelectedProp(null); setPropSearch(""); setPropResults([]); setForm(f=>({...f,propertyId:"",clientName:"",clientStreet:"",clientUnit:"",clientCity:"",clientState:"NY",clientZip:""})); setSelectedClientId(null); setClientSearch(""); setAddNewClient(false); setNewClientForm({firstName:"",lastName:"",notes:""}); setClientDupExact([]); setClientDupFuzzy([]); setContacts([]); setCreateNewProperty(false); setNewPropForm({propertyType:"Single-Family",status:"Needs Work",keyStatus:"Keys Available"}); setPrimaryContact(""); setAddUnlistedContact(false); setUnlistedContact({name:"",relationship:"",phone:"",email:""}); setDupCandidates([]); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setErr(""); setLoading(true);
    try {
      let finalClientId = selectedClientId || null;
      let finalClientName = form.clientName;
      if (!selectedClientId && addNewClient && newClientForm.firstName.trim() && newClientForm.lastName.trim()) {
        try {
          const newClient = await clientsAPI.create(newClientForm);
          setClients(cs=>[...cs, newClient]);
          finalClientId = newClient.id;
          finalClientName = newClient.name; // use the name the backend assigned
        } catch(ex) {
          // 409 from backend means an exact-normalized duplicate exists. Surface it
          // and let the user pick from the existing matches via the warning panel.
          if (ex.message && ex.message.toLowerCase().includes("already exists")) {
            try {
              const matches=await clientsAPI.check({firstName:newClientForm.firstName, lastName:newClientForm.lastName});
              setClientDupExact(matches.exact||[]);
              setClientDupFuzzy(matches.fuzzy||[]);
            } catch{}
            setLoading(false);
            setErr("A client with this name already exists. Please use the existing record or change the name.");
            return;
          }
          throw ex;
        }
      }
      // If "save as new property" checked, create the property first and use its id
      let finalPropertyId = form.propertyId || "";
      if (!selectedProp && createNewProperty && form.clientStreet.trim() && form.clientCity.trim()) {
        const newProp = await propertiesAPI.create({
          street:form.clientStreet, unit:form.clientUnit, city:form.clientCity,
          state:form.clientState, zip:form.clientZip,
          propertyType:newPropForm.propertyType, status:newPropForm.status, keyStatus:newPropForm.keyStatus,
          clientIds: finalClientId ? [finalClientId] : [],
          contacts: contacts.filter(c=>c.name.trim()).map(c=>({...c,phones:c.phone?[{label:"",number:c.phone}]:[],emails:c.email?[{label:"",address:c.email}]:[]})),
          notes:"",
        });
        finalPropertyId = newProp.id;
      }
      // Use primary contact name as clientName when property is selected
      const effectiveClientName = selectedProp ? (primaryContact || finalClientName) : finalClientName;
      await ticketsAPI.create({...form,clientName:effectiveClientName,primaryContact:selectedProp?primaryContact:"",propertyId:finalPropertyId,outsideContractor:String(form.outsideContractor),clientId:finalClientId||"",contacts:JSON.stringify(contacts.filter(c=>c.name.trim()))},photoFile);
      setSuccess(true); setForm(blank); setPhotoFile(null); setSelectedProp(null); setPropSearch(""); setSelectedClientId(null); setClientSearch(""); setAddNewClient(false); setNewClientForm({firstName:"",lastName:"",notes:""}); setContacts([]); setCreateNewProperty(false); setNewPropForm({propertyType:"Single-Family",status:"Needs Work",keyStatus:"Keys Available"}); setPrimaryContact(""); setAddUnlistedContact(false); setUnlistedContact({name:"",relationship:"",phone:"",email:""}); setDupCandidates([]);
      if(photoRef.current)photoRef.current.value="";
    }
    catch(ex) { setErr(ex.message); }
    finally { setLoading(false); submittingRef.current = false; }
  }

  if (success) return (
    <div style={{maxWidth:560,margin:"60px auto",textAlign:"center",padding:24}} className="page-enter">
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:28,fontWeight:700,marginBottom:8,color:G.text}}>Ticket Submitted</div>
      <p style={{color:G.muted,marginBottom:24}}>Your maintenance request has been logged.</p>
      <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}><Btn onClick={()=>setSuccess(false)} G={G}>Submit Another</Btn><Btn variant="secondary" onClick={onSaved} G={G}>Go to Dashboard</Btn></div>
    </div>
  );
  return (
    <div style={{maxWidth:700,margin:"0 auto",padding:isMobile?"16px":"32px 16px"}} className="page-enter">
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:isMobile?26:34,letterSpacing:".04em",marginBottom:28,color:G.text}}><span style={{color:G.accent}}>//</span> NEW MAINTENANCE TICKET</div>
      <div style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,padding:isMobile?16:28}}>
        <Alert msg={err} G={G} />
        <form onSubmit={handleSubmit}>
          {/* Property selector */}
          <div style={{background:G.bg,border:`1px solid ${selectedProp?G.accent:G.border}`,borderRadius:G.radius,padding:"14px 16px",marginBottom:16,position:"relative"}}>
            <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.accent,marginBottom:10}}>Property</div>
            {selectedProp ? (
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                <div>
                  <div style={{fontWeight:600,color:G.text,fontSize:14}}>{[selectedProp.street,selectedProp.unit].filter(Boolean).join(" ")}</div>
                  <div style={{fontSize:12,color:G.muted}}>{[selectedProp.city,selectedProp.state,selectedProp.zip].filter(Boolean).join(", ")}</div>
                  {selectedProp.clientNames?.length>0&&<div style={{fontSize:12,color:G.accent,marginTop:2}}>Client: {selectedProp.clientNames.join(", ")}</div>}
                </div>
                <Btn size="sm" variant="ghost" onClick={clearProperty} G={G}>Change</Btn>
              </div>
            ) : (
              <div style={{position:"relative"}}>
                <input value={propSearch} onChange={e=>handlePropSearch(e.target.value)} onFocus={()=>propSearch&&setShowPropDropdown(true)} placeholder="Search by address or client name..." style={{paddingRight:32}} />
                {propSearching&&<div style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",width:16,height:16,border:`2px solid ${G.border}`,borderTopColor:G.accent,borderRadius:"50%",animation:"spin .7s linear infinite"}} />}
                {showPropDropdown&&propResults.length>0&&(
                  <div style={{position:"absolute",top:"100%",left:0,right:0,background:G.surface,border:`1px solid ${G.border}`,borderRadius:G.radius,zIndex:20,maxHeight:260,overflowY:"auto",marginTop:4,boxShadow:"0 8px 24px rgba(0,0,0,.4)"}}>
                    {propResults.map(p=>(
                      <div key={p.id} onClick={()=>selectProperty(p)} style={{padding:"10px 14px",cursor:"pointer",borderBottom:`1px solid ${G.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}} onMouseEnter={e=>e.currentTarget.style.background=G.accent+"18"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                        <div>
                          <div style={{fontWeight:600,fontSize:13,color:G.text}}>{[p.street,p.unit].filter(Boolean).join(" ")}</div>
                          <div style={{fontSize:12,color:G.muted}}>{[p.city,p.state,p.zip].filter(Boolean).join(", ")}</div>
                          {p.clientNames?.length>0&&<div style={{fontSize:11,color:G.accent}}>{p.clientNames.join(", ")}</div>}                        </div>
                        <Badge text={p.status} color={PROPERTY_STATUS_COLORS[p.status]||G.muted} />
                      </div>
                    ))}
                  </div>
                )}
                {showPropDropdown&&propSearch&&!propSearching&&propResults.length===0&&(
                  <div style={{position:"absolute",top:"100%",left:0,right:0,background:G.surface,border:`1px solid ${G.border}`,borderRadius:G.radius,zIndex:20,padding:"12px 14px",fontSize:13,color:G.muted,marginTop:4}}>No properties found</div>
                )}
              </div>
            )}
          </div>
          {/* Client section */}
          <div style={{background:G.bg,border:`1px solid ${selectedClientId?G.accent:G.border}`,borderRadius:G.radius,padding:"14px 16px",marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.accent,marginBottom:10}}>Client (Optional)</div>
            {selectedClientId ? (
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
                <div style={{fontWeight:600,color:G.text,fontSize:14}}>{clients.find(c=>c.id===selectedClientId)?.name||clientSearch}</div>
                <Btn size="sm" variant="ghost" onClick={()=>{setSelectedClientId(null);setClientSearch("");setAddNewClient(false);}} G={G}>Change</Btn>
              </div>
            ) : !addNewClient ? (
              <div>
                <div style={{position:"relative",marginBottom:8}}>
                  <input value={clientSearch} onChange={e=>{setClientSearch(e.target.value);setShowClientDropdown(true);}} onFocus={()=>clientSearch&&setShowClientDropdown(true)} placeholder="Search by client name..." />
                  {showClientDropdown&&clientSearch&&(()=>{const filtered=clients.filter(c=>c.name.toLowerCase().includes(clientSearch.toLowerCase())).slice(0,8);return filtered.length>0?(
                    <div style={{position:"absolute",top:"100%",left:0,right:0,background:G.surface,border:`1px solid ${G.border}`,borderRadius:G.radius,zIndex:20,maxHeight:220,overflowY:"auto",marginTop:4,boxShadow:"0 8px 24px rgba(0,0,0,.4)"}}>
                      {filtered.map(c=>(
                        <div key={c.id} onClick={()=>{setSelectedClientId(c.id);setClientSearch(c.name);setShowClientDropdown(false);setForm(f=>({...f,clientName:c.name}));}} style={{padding:"9px 14px",cursor:"pointer",borderBottom:`1px solid ${G.border}`,fontSize:13,color:G.text}} onMouseEnter={e=>e.currentTarget.style.background=G.accent+"18"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                          <span style={{fontWeight:600}}>{c.name}</span>{c.clientNumber?<span style={{color:G.muted,marginLeft:8,fontSize:11}}>#{c.clientNumber}</span>:null}
                        </div>
                      ))}
                    </div>
                  ):(<div style={{position:"absolute",top:"100%",left:0,right:0,background:G.surface,border:`1px solid ${G.border}`,borderRadius:G.radius,zIndex:20,padding:"10px 14px",fontSize:13,color:G.muted,marginTop:4}}>No clients found</div>);})()}
                </div>
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",textTransform:"none",fontSize:13,color:G.muted,fontWeight:400,letterSpacing:"normal",margin:0,padding:"7px 12px",background:"transparent",border:`1px solid ${G.border}`,borderRadius:G.radius,userSelect:"none",width:"fit-content"}}>
                  <input type="checkbox" checked={addNewClient} onChange={e=>{setAddNewClient(e.target.checked);setClientSearch("");setSelectedClientId(null);}} style={{width:14,height:14,accentColor:G.accent}} />
                  ✚ New Client
                </label>
              </div>
            ) : (
              <div>
                <div style={{marginBottom:10,padding:"12px 14px",background:G.accent+"10",borderRadius:G.radius,border:`1px solid ${G.accent}44`}}>
                  <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.accent,marginBottom:10}}>New Client Details</div>
                  <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12}}>
                    <Field label="First Name" required G={G}><input value={newClientForm.firstName} onChange={e=>setNewClientForm({...newClientForm,firstName:e.target.value})} placeholder="Jane" /></Field>
                    <Field label="Last Name" required G={G}><input value={newClientForm.lastName} onChange={e=>setNewClientForm({...newClientForm,lastName:e.target.value})} placeholder="Smith" /></Field>
                  </div>
                  {/* Exact duplicate — hard block, offer to use existing */}
                  {clientDupExact.length>0&&(
                    <div style={{padding:"10px 12px",background:"#ef444418",border:`1px solid #ef4444`,borderRadius:G.radius,marginBottom:10}}>
                      <div style={{fontSize:12,fontWeight:700,color:"#ef4444",marginBottom:6}}>⚠ This client already exists</div>
                      {clientDupExact.map(c=>(
                        <div key={c.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 10px",background:G.surface,borderRadius:G.radius,marginBottom:4,gap:10}}>
                          <div style={{fontSize:13,fontWeight:600,color:G.text}}>{c.name}</div>
                          <Btn size="sm" type="button" onClick={()=>{ setSelectedClientId(c.id); setClientSearch(c.name); setAddNewClient(false); setNewClientForm({firstName:"",lastName:"",notes:""}); setClientDupExact([]); setClientDupFuzzy([]); setForm(f=>({...f,clientName:c.name})); }} G={G}>Use This</Btn>
                        </div>
                      ))}
                      <div style={{fontSize:11,color:G.muted,marginTop:4}}>Change the name above if this is a different person.</div>
                    </div>
                  )}
                  {/* Fuzzy match — soft warn, allow submit */}
                  {clientDupExact.length===0&&clientDupFuzzy.length>0&&(
                    <div style={{padding:"10px 12px",background:"#eab30818",border:`1px solid #eab308`,borderRadius:G.radius,marginBottom:10}}>
                      <div style={{fontSize:12,fontWeight:700,color:"#eab308",marginBottom:6}}>Did you mean one of these?</div>
                      {clientDupFuzzy.map(c=>(
                        <div key={c.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 10px",background:G.surface,borderRadius:G.radius,marginBottom:4,gap:10}}>
                          <div style={{fontSize:13,fontWeight:600,color:G.text}}>{c.name}</div>
                          <Btn size="sm" type="button" onClick={()=>{ setSelectedClientId(c.id); setClientSearch(c.name); setAddNewClient(false); setNewClientForm({firstName:"",lastName:"",notes:""}); setClientDupExact([]); setClientDupFuzzy([]); setForm(f=>({...f,clientName:c.name})); }} G={G}>Use This</Btn>
                        </div>
                      ))}
                      <div style={{fontSize:11,color:G.muted,marginTop:4}}>Or continue creating a new client below if these aren't a match.</div>
                    </div>
                  )}
                  <Field label="Notes" G={G}><textarea rows={2} value={newClientForm.notes} onChange={e=>setNewClientForm({...newClientForm,notes:e.target.value})} placeholder="Any relevant notes" style={{resize:"vertical"}} /></Field>
                </div>
                <button type="button" onClick={()=>{setAddNewClient(false);setNewClientForm({firstName:"",lastName:"",notes:""});}} style={{fontSize:12,color:G.muted,background:"none",border:"none",cursor:"pointer",padding:0,textDecoration:"underline"}}>← Search existing clients instead</button>
              </div>
            )}
          </div>
          {/* Primary Contact (when property selected) or Client Name / Phone (manual) */}
          {selectedProp ? (
            <div style={{background:G.bg,border:`1px solid ${G.border}`,borderRadius:G.radius,padding:"14px 16px",marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.accent,marginBottom:10}}>Primary Contact</div>
              {!addUnlistedContact ? (
                <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
                  <select value={primaryContact} onChange={e=>setPrimaryContact(e.target.value)} style={{flex:1,minWidth:180}}>
                    <option value="">— Select a contact —</option>
                    {(selectedProp.clientNames||[]).map((name,i)=>(
                      <option key={"client-"+i} value={name}>{name} (Client)</option>
                    ))}
                    {(selectedProp.contacts||[]).filter(c=>c.name).map((c,i)=>(
                      <option key={"contact-"+i} value={c.name}>{c.name}{c.relationship?` — ${c.relationship}`:""}</option>
                    ))}
                  </select>
                  <button type="button" onClick={()=>setAddUnlistedContact(true)} style={{background:"none",border:`1px solid ${G.border}`,color:G.muted,borderRadius:G.radius,padding:"9px 14px",cursor:"pointer",fontSize:13,whiteSpace:"nowrap",flexShrink:0}}>+ Contact not listed</button>
                </div>
              ) : (
                <div>
                  <div style={{padding:"12px 14px",background:G.accent+"10",borderRadius:G.radius,border:`1px solid ${G.accent}44`,marginBottom:10}}>
                    <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.accent,marginBottom:10}}>New Contact</div>
                    <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:10,marginBottom:10}}>
                      <Field label="Name" required G={G}><input value={unlistedContact.name} onChange={e=>setUnlistedContact(c=>({...c,name:e.target.value}))} placeholder="Contact name" /></Field>
                      <Field label="Relationship" G={G}><input value={unlistedContact.relationship} onChange={e=>setUnlistedContact(c=>({...c,relationship:e.target.value}))} placeholder="e.g. Property Manager" /></Field>
                    </div>
                    <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:10,marginBottom:12}}>
                      <Field label="Phone" G={G}><input value={unlistedContact.phone} onChange={e=>setUnlistedContact(c=>({...c,phone:e.target.value}))} placeholder="(555) 555-5555" /></Field>
                      <Field label="Email" G={G}><input type="email" value={unlistedContact.email} onChange={e=>setUnlistedContact(c=>({...c,email:e.target.value}))} placeholder="email@example.com" /></Field>
                    </div>
                    <div style={{display:"flex",gap:10}}>
                      <Btn size="sm" loading={savingUnlisted} onClick={async()=>{
                        if (!unlistedContact.name.trim()) return;
                        setSavingUnlisted(true);
                        try {
                          const newContact = {id:"CON-"+Date.now().toString(36).toUpperCase(), ...unlistedContact, phones:unlistedContact.phone?[{label:"",number:unlistedContact.phone}]:[], emails:unlistedContact.email?[{label:"",address:unlistedContact.email}]:[]};
                          const updatedContacts = [...(selectedProp.contacts||[]), newContact];
                          await propertiesAPI.update(selectedProp.id, {contacts:updatedContacts});
                          setSelectedProp(p=>({...p,contacts:updatedContacts}));
                          setPrimaryContact(unlistedContact.name);
                          setAddUnlistedContact(false);
                          setUnlistedContact({name:"",relationship:"",phone:"",email:""});
                        } catch(ex) { setErr(ex.message); }
                        finally { setSavingUnlisted(false); }
                      }} G={G} type="button">Save & Select</Btn>
                      <Btn size="sm" variant="ghost" type="button" onClick={()=>{setAddUnlistedContact(false);setUnlistedContact({name:"",relationship:"",phone:"",email:""});}} G={G}>Cancel</Btn>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:16,marginBottom:16}}>
              {!addNewClient&&<Field label="Client Name" required G={G}><input value={form.clientName} onChange={e=>setForm({...form,clientName:e.target.value})} placeholder="Full name" /></Field>}
              {addNewClient&&(
                <div style={{background:G.bg,border:`1px solid ${G.border}`,borderRadius:G.radius,padding:"9px 12px"}}>
                  <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.muted,marginBottom:3}}>Client Name</div>
                  <div style={{fontSize:14,color:newClientForm.firstName||newClientForm.lastName?G.text:G.muted,fontStyle:newClientForm.firstName||newClientForm.lastName?"normal":"italic"}}>
                    {newClientForm.firstName||newClientForm.lastName?[newClientForm.firstName,newClientForm.lastName].filter(Boolean).join(" "):"Will be set from new client above"}
                  </div>
                </div>
              )}
              <Field label="Contact Phone" G={G}><input value={form.clientPhone} onChange={e=>setForm({...form,clientPhone:e.target.value})} placeholder="(555) 555-5555" /></Field>
            </div>
          )}
          {!selectedProp&&(
            <div style={{background:G.bg,border:`1px solid ${G.border}`,borderRadius:G.radius,padding:"14px 16px",marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.muted,marginBottom:12}}>Service Address (manual — select a property above to auto-fill)</div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"2fr 1fr",gap:12}}>
                <Field label="Street Address" G={G}><input value={form.clientStreet} onChange={e=>setForm({...form,clientStreet:e.target.value})} placeholder="123 Main Street" /></Field>
                <Field label="Unit / Apt" G={G}><input value={form.clientUnit} onChange={e=>setForm({...form,clientUnit:e.target.value})} placeholder="Apt 4B" /></Field>
              </div>
              <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"2fr 1fr 1fr",gap:12}}>
                <Field label="City" G={G}><input value={form.clientCity} onChange={e=>setForm({...form,clientCity:e.target.value})} placeholder="New York" /></Field>
                <Field label="State" G={G}><input value={form.clientState} onChange={e=>setForm({...form,clientState:e.target.value})} placeholder="NY" /></Field>
                <Field label="Zip Code" G={G}><input value={form.clientZip} onChange={e=>setForm({...form,clientZip:e.target.value})} placeholder="10001" /></Field>
              </div>
              {/* Contacts */}
              <div style={{marginTop:14,borderTop:`1px solid ${G.border}`,paddingTop:14}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                  <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.muted}}>Contacts</div>
                  <Btn size="sm" variant="ghost" type="button" onClick={addContact} G={G}>+ Add Contact</Btn>
                </div>
                {contacts.length===0&&<div style={{fontSize:12,color:G.muted,fontStyle:"italic"}}>No contacts added — use "+ Add Contact" to add a property manager, family member, etc.</div>}
                {contacts.map((c,i)=>(
                  <div key={i} style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"2fr 1fr 1fr 1fr auto",gap:10,marginBottom:10,alignItems:"end"}}>
                    <Field label="Name" G={G}><input value={c.name} onChange={e=>updateContact(i,"name",e.target.value)} placeholder="Contact name" /></Field>
                    <Field label="Relationship" G={G}><input value={c.relationship} onChange={e=>updateContact(i,"relationship",e.target.value)} placeholder="e.g. Property Mgr" /></Field>
                    <Field label="Phone" G={G}><input value={c.phone} onChange={e=>updateContact(i,"phone",e.target.value)} onBlur={e=>updateContact(i,"phone",formatPhone(e.target.value))} placeholder="(555) 555-5555" /></Field>
                    <Field label="Email" G={G}><input type="email" value={c.email} onChange={e=>updateContact(i,"email",e.target.value)} placeholder="email@example.com" /></Field>
                    <button type="button" onClick={()=>removeContact(i)} style={{background:"none",border:`1px solid ${G.border}`,color:G.muted,borderRadius:G.radius,padding:"9px 12px",cursor:"pointer",fontSize:16,lineHeight:1,marginBottom:1}}>✕</button>
                  </div>
                ))}
              </div>
              {/* Save as new property */}
              <div style={{marginTop:14,borderTop:`1px solid ${G.border}`,paddingTop:14}}>
                <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",textTransform:"none",fontSize:13,color:createNewProperty?G.accent:G.muted,fontWeight:createNewProperty?700:400,letterSpacing:"normal",margin:0,userSelect:"none"}}>
                  <input type="checkbox" checked={createNewProperty} onChange={e=>setCreateNewProperty(e.target.checked)} style={{width:15,height:15,accentColor:G.accent}} />
                  Also save this address as a new property record
                </label>
                {createNewProperty&&dupCandidates.length>0&&(
                  <div style={{marginTop:10,padding:"12px 14px",background:"#ef444418",border:`1px solid #ef4444`,borderRadius:G.radius}}>
                    <div style={{fontSize:12,fontWeight:700,color:"#ef4444",marginBottom:8}}>⚠ Possible duplicate {dupCandidates.length===1?"property":"properties"} found</div>
                    {dupCandidates.map(p=>(
                      <div key={p.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",background:G.surface,borderRadius:G.radius,marginBottom:6,gap:10}}>
                        <div>
                          <div style={{fontSize:13,fontWeight:600,color:G.text}}>{[p.street,p.unit].filter(Boolean).join(" ")}</div>
                          <div style={{fontSize:12,color:G.muted}}>{[p.city,p.state,p.zip].filter(Boolean).join(", ")} · <span style={{color:PROPERTY_STATUS_COLORS[p.status]||G.muted}}>{p.status}</span></div>
                        </div>
                        <Btn size="sm" type="button" onClick={()=>{ selectProperty(p); setCreateNewProperty(false); setDupCandidates([]); }} G={G}>Use This</Btn>
                      </div>
                    ))}
                    <div style={{fontSize:11,color:G.muted,marginTop:6}}>You can still save as new property if this is a different address.</div>
                  </div>
                )}
                {createNewProperty&&(
                  <>
                    <div style={{marginTop:10,display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12}}>
                      <Field label="Property Type" G={G}><select value={newPropForm.propertyType} onChange={e=>setNewPropForm({...newPropForm,propertyType:e.target.value})}>{PROPERTY_TYPES.map(t=><option key={t}>{t}</option>)}</select></Field>
                      <Field label="Property Status" G={G}><select value={newPropForm.status} onChange={e=>setNewPropForm({...newPropForm,status:e.target.value})}>{ACTIVE_PROPERTY_STATUSES.map(s=><option key={s}>{s}</option>)}</select></Field>
                    </div>
                    <Field label="Key Status" required G={G}>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        {KEY_STATUSES.map(k=>(
                          <label key={k} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 12px",background:newPropForm.keyStatus===k?G.accent+"22":G.surface,border:`1px solid ${newPropForm.keyStatus===k?G.accent:G.border}`,borderRadius:G.radius,cursor:"pointer",textTransform:"none",fontSize:13,color:G.text,fontWeight:newPropForm.keyStatus===k?600:400,letterSpacing:"normal",margin:0,flex:"1 1 180px"}}>
                            <input type="radio" name="newprop-keyStatus" value={k} checked={newPropForm.keyStatus===k} onChange={()=>setNewPropForm(f=>({...f,keyStatus:k}))} />
                            {k}
                          </label>
                        ))}
                      </div>
                    </Field>
                  </>
                )}
              </div>
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr 1fr",gap:16}}>
            <Field label="Date Reported" required G={G}><input type="date" value={form.dateReported} onChange={e=>setForm({...form,dateReported:e.target.value})} /></Field>
            <Field label="Work Type" required G={G}><select value={form.workType} onChange={e=>setForm({...form,workType:e.target.value})}>{WORK_TYPES.map(w=><option key={w}>{w}</option>)}</select></Field>
            <Field label="Urgency Level" required G={G}><select value={form.urgency} onChange={e=>setForm({...form,urgency:e.target.value})}>{URGENCY.map(u=><option key={u}>{u}</option>)}</select></Field>
          </div>
          <Field label="Description of Work Needed" required G={G}><textarea rows={4} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Describe the issue and work required..." style={{resize:"vertical"}} /></Field>
          <Field label="Photo (Optional)" G={G}><input ref={photoRef} type="file" accept="image/*" onChange={e=>setPhotoFile(e.target.files[0]||null)} style={{padding:"7px 12px"}} /></Field>
          <div style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",background:G.bg,borderRadius:G.radius,border:`1px solid ${G.border}`,marginBottom:24}}>
            <input type="checkbox" id="oc" checked={form.outsideContractor} onChange={e=>setForm({...form,outsideContractor:e.target.checked})} style={{width:18,height:18}} />
            <label htmlFor="oc" style={{margin:0,textTransform:"none",fontSize:14,color:G.text,cursor:"pointer"}}>Outside Contractor Needed?</label>
          </div>
          <Btn size="lg" style={{width:"100%"}} loading={loading} G={G}>Submit Ticket</Btn>
        </form>
      </div>
    </div>
  );
}

// ── Ticket Card + Detail ──────────────────────────────────────────────────────
// ── BidRow ───────────────────────────────────────────────────────────────────
function BidRow({ bid, ticket, G, onUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const BID_STATUS_COLORS = { pending:"#f59e0b", accepted:"#10b981", declined:"#ef4444", withdrawn:"#6b7280", competition:"#a855f7" };
  async function doAction(action) {
    setSaving(true); setErr("");
    try { await bidsAPI.action(ticket.id, bid.id, action, note); setExpanded(false); setNote(""); onUpdated(); }
    catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }
  const canAct = ["pending","competition"].includes(bid.status);
  return (
    <div style={{background:G.bg,border:`1px solid ${G.border}`,borderRadius:G.radius,padding:"14px 16px",marginBottom:10}}>
      <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <div style={{flex:1}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
            <span style={{fontWeight:700,color:G.text}}>{bid.contractorName}</span>
            <Badge text={bid.status} color={BID_STATUS_COLORS[bid.status]||G.muted} />
            {bid.status==="competition"&&bid.competitionWindowEnd&&<span style={{fontSize:11,color:"#a855f7"}}>Window closes: {fmtDate(bid.competitionWindowEnd)}</span>}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
            <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:22,color:G.accent}}>${bid.amount.toLocaleString()}</span>
            {bid.note&&<span style={{fontSize:13,color:G.muted,fontStyle:"italic"}}>{bid.note}</span>}
          </div>
          {bid.adminNote&&<div style={{fontSize:12,color:G.muted,marginTop:4}}>Admin note: {bid.adminNote}</div>}
        </div>
        {canAct&&<Btn size="sm" variant="secondary" onClick={()=>setExpanded(v=>!v)} G={G}>{expanded?"Cancel":"Take Action"}</Btn>}
      </div>
      {expanded&&(
        <div style={{marginTop:14,paddingTop:14,borderTop:`1px solid ${G.border}`}}>
          <Alert msg={err} G={G} />
          <Field label="Optional Note to Contractor" G={G}><textarea rows={2} value={note} onChange={e=>setNote(e.target.value)} placeholder="Add context for the contractor..." style={{resize:"vertical"}} /></Field>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <Btn size="sm" variant="success" onClick={()=>doAction("accept")} loading={saving} G={G}>Accept Immediately</Btn>
            <Btn size="sm" style={{background:"#a855f7",color:"#fff"}} onClick={()=>doAction("competition")} loading={saving} G={G}>Accept + Open Competition</Btn>
            <Btn size="sm" variant="danger" onClick={()=>doAction("decline")} loading={saving} G={G}>Decline</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

// ── QAThread ──────────────────────────────────────────────────────────────────
function QAThread({ q, ticket, G, onUpdated }) {
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  async function sendReply(e) {
    e.preventDefault(); setErr(""); setSaving(true);
    try { await questionsAPI.reply(ticket.id, q.id, reply); setReply(""); onUpdated(); }
    catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }
  return (
    <div style={{background:G.bg,border:`1px solid ${G.border}`,borderRadius:G.radius,padding:"14px 16px",marginBottom:10}}>
      <div style={{fontSize:12,color:G.muted,marginBottom:8,fontWeight:600}}>{q.contractorName}</div>
      {q.thread.map((msg,i)=>(
        <div key={i} style={{display:"flex",gap:10,marginBottom:10,flexDirection:msg.from==="admin"?"row-reverse":"row"}}>
          <div style={{maxWidth:"80%",background:msg.from==="admin"?G.accent+"22":G.surface,border:`1px solid ${msg.from==="admin"?G.accent+"44":G.border}`,borderRadius:G.radius,padding:"8px 12px"}}>
            <div style={{fontSize:11,color:G.muted,marginBottom:4}}>{msg.from==="admin"?"Allied CSS":msg.name} · {new Date(msg.at).toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}</div>
            <div style={{fontSize:13,color:G.text}}>{msg.message}</div>
          </div>
        </div>
      ))}
      <Alert msg={err} G={G} />
      <form onSubmit={sendReply} style={{display:"flex",gap:8,marginTop:8}}>
        <input value={reply} onChange={e=>setReply(e.target.value)} placeholder="Reply as Allied CSS..." style={{flex:1}} />
        <Btn loading={saving} G={G}>Reply</Btn>
      </form>
    </div>
  );
}

// ── LocksmithAssignField ─────────────────────────────────────────────────────
// Typeahead over approved Locksmithing-services contractors AND temp
// contractors, plus an inline "+ Add temp locksmith" form. Used in the Allied
// view of an Investigate ticket whose keyStatus is "Locksmith Required".
function LocksmithAssignField({ ticket, onUpdated, G }) {
  const isMobile = useIsMobile();
  const [search, setSearch] = useState("");
  const [contractorOpts, setContractorOpts] = useState([]);
  const [tempOpts, setTempOpts] = useState([]);
  const [picking, setPicking] = useState(!ticket.assignedLocksmithId);
  const [showAddTemp, setShowAddTemp] = useState(false);
  const [tempForm, setTempForm] = useState({ companyName:"", email:"", phone:"" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const searchTimer = useRef();

  useEffect(() => {
    contractorsAPI.list()
      .then(list => setContractorOpts((list||[]).filter(c => (c.contractorProfile?.services||[]).includes("Locksmithing"))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!picking) return;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      tempContractorsAPI.list(search).then(setTempOpts).catch(() => setTempOpts([]));
    }, 250);
  }, [search, picking]);

  async function assign(id) {
    setSaving(true); setErr("");
    try {
      const updated = await ticketsAPI.patchFields(ticket.id, { assignedLocksmithId: id });
      setSearch(""); setPicking(false); setShowAddTemp(false);
      onUpdated(updated);
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  async function unassign() {
    setSaving(true); setErr("");
    try {
      const updated = await ticketsAPI.patchFields(ticket.id, { assignedLocksmithId: null });
      onUpdated(updated);
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  async function saveTempAndAssign(e) {
    e.preventDefault(); setErr("");
    if (!tempForm.companyName.trim() || !tempForm.email.trim()) {
      setErr("Company name and email are required."); return;
    }
    setSaving(true);
    try {
      const newTemp = await tempContractorsAPI.create(tempForm);
      const updated = await ticketsAPI.patchFields(ticket.id, { assignedLocksmithId: newTemp.id });
      setTempForm({ companyName:"", email:"", phone:"" });
      setShowAddTemp(false); setPicking(false); setSearch("");
      onUpdated(updated);
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  const sLower = search.trim().toLowerCase();
  const filteredContractors = sLower
    ? contractorOpts.filter(c => ((c.contractorProfile?.companyName)||c.name||"").toLowerCase().includes(sLower))
    : contractorOpts;

  return (
    <div style={{background:G.bg,border:`1px solid ${G.border}`,borderRadius:G.radius,padding:"12px 14px",marginBottom:10}}>
      <div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",fontWeight:700,marginBottom:8}}>Locksmith</div>
      {err && <Alert msg={err} G={G} />}
      {ticket.assignedLocksmithId && !picking ? (
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
          <div>
            <div style={{fontWeight:600,color:G.text,fontSize:14}}>{ticket.assignedLocksmithName || "—"}</div>
            {String(ticket.assignedLocksmithId).startsWith("TEMP-") && <div style={{fontSize:11,color:G.muted,marginTop:2}}>Temp record · pending registration</div>}
          </div>
          <div style={{display:"flex",gap:6}}>
            <Btn size="sm" variant="ghost" onClick={()=>setPicking(true)} G={G}>Change</Btn>
            <Btn size="sm" variant="danger" onClick={unassign} loading={saving} G={G}>Unassign</Btn>
          </div>
        </div>
      ) : showAddTemp ? (
        <form onSubmit={saveTempAndAssign}>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:10,marginBottom:10}}>
            <Field label="Company Name" required G={G}><input value={tempForm.companyName} onChange={e=>setTempForm(f=>({...f,companyName:e.target.value}))} placeholder="ABC Locksmith Inc." /></Field>
            <Field label="Email" required G={G}><input type="email" value={tempForm.email} onChange={e=>setTempForm(f=>({...f,email:e.target.value}))} placeholder="contact@example.com" /></Field>
          </div>
          <Field label="Phone (Optional)" G={G}><input value={tempForm.phone} onChange={e=>setTempForm(f=>({...f,phone:e.target.value}))} onBlur={e=>setTempForm(f=>({...f,phone:formatPhone(e.target.value)}))} placeholder="(555) 555-5555" /></Field>
          <div style={{display:"flex",gap:8}}>
            <Btn size="sm" loading={saving} G={G}>Save & Assign</Btn>
            <Btn size="sm" variant="ghost" type="button" onClick={()=>{setShowAddTemp(false);setTempForm({companyName:"",email:"",phone:""});}} G={G}>Cancel</Btn>
          </div>
        </form>
      ) : (
        <div>
          <div style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap"}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by company name..." style={{flex:1,minWidth:200}} />
            <Btn size="sm" variant="secondary" onClick={()=>setShowAddTemp(true)} G={G}>+ Add Temp</Btn>
            {ticket.assignedLocksmithId && <Btn size="sm" variant="ghost" onClick={()=>setPicking(false)} G={G}>Cancel</Btn>}
          </div>
          <div style={{maxHeight:220,overflowY:"auto",display:"flex",flexDirection:"column",gap:4}}>
            {filteredContractors.map(c => (
              <button key={c.id} type="button" onClick={()=>assign(c.id)} disabled={saving} style={{textAlign:"left",padding:"8px 12px",background:G.surface,border:`1px solid ${G.border}`,borderRadius:G.radius,color:G.text,cursor:"pointer",fontSize:13,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                <span><span style={{fontWeight:600}}>{c.contractorProfile?.companyName || c.name}</span>{c.contractorProfile?.companyName && <span style={{color:G.muted,marginLeft:6,fontSize:12}}>· {c.name}</span>}</span>
                <span style={{fontSize:10,color:G.success,letterSpacing:".06em",textTransform:"uppercase"}}>Approved</span>
              </button>
            ))}
            {tempOpts.map(t => (
              <button key={t.id} type="button" onClick={()=>assign(t.id)} disabled={saving} style={{textAlign:"left",padding:"8px 12px",background:G.surface,border:`1px solid ${G.border}`,borderRadius:G.radius,color:G.text,cursor:"pointer",fontSize:13,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                <span><span style={{fontWeight:600}}>{t.companyName}</span><span style={{color:G.muted,marginLeft:6,fontSize:12}}>· {t.email}</span></span>
                <span style={{fontSize:10,color:G.muted,letterSpacing:".06em",textTransform:"uppercase"}}>Temp</span>
              </button>
            ))}
            {filteredContractors.length===0 && tempOpts.length===0 && (
              <div style={{fontSize:13,color:G.muted,padding:"8px 12px",fontStyle:"italic"}}>{search?"No matches. Use \"+ Add Temp\" to add a new locksmith.":"No locksmiths yet. Use \"+ Add Temp\" to add one."}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── AlliedAssigneeField ──────────────────────────────────────────────────────
// Typeahead over active Allied users (any allied role). Records who from
// Allied was on-site for this ticket. Optional; can be cleared.
function AlliedAssigneeField({ ticket, onUpdated, G }) {
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [picking, setPicking] = useState(!ticket.alliedAssignee);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const searchTimer = useRef();

  useEffect(() => {
    if (!picking) return;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      usersAPI.listAllied(search).then(setOptions).catch(() => setOptions([]));
    }, 250);
  }, [search, picking]);

  async function assign(id) {
    setSaving(true); setErr("");
    try {
      const updated = await ticketsAPI.patchFields(ticket.id, { alliedAssignee: id });
      setSearch(""); setPicking(false);
      onUpdated(updated);
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  async function unassign() {
    setSaving(true); setErr("");
    try {
      const updated = await ticketsAPI.patchFields(ticket.id, { alliedAssignee: null });
      onUpdated(updated);
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{background:G.bg,border:`1px solid ${G.border}`,borderRadius:G.radius,padding:"12px 14px",marginBottom:10}}>
      <div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",fontWeight:700,marginBottom:8}}>Allied Assignee (On-Site)</div>
      {err && <Alert msg={err} G={G} />}
      {ticket.alliedAssignee && !picking ? (
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
          <div style={{fontWeight:600,color:G.text,fontSize:14}}>{ticket.alliedAssigneeName || "—"}</div>
          <div style={{display:"flex",gap:6}}>
            <Btn size="sm" variant="ghost" onClick={()=>setPicking(true)} G={G}>Change</Btn>
            <Btn size="sm" variant="danger" onClick={unassign} loading={saving} G={G}>Clear</Btn>
          </div>
        </div>
      ) : (
        <div>
          <div style={{display:"flex",gap:8,marginBottom:8,flexWrap:"wrap"}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search Allied staff by name..." style={{flex:1,minWidth:200}} />
            {ticket.alliedAssignee && <Btn size="sm" variant="ghost" onClick={()=>setPicking(false)} G={G}>Cancel</Btn>}
          </div>
          <div style={{maxHeight:200,overflowY:"auto",display:"flex",flexDirection:"column",gap:4}}>
            {options.map(u => (
              <button key={u.id} type="button" onClick={()=>assign(u.id)} disabled={saving} style={{textAlign:"left",padding:"8px 12px",background:G.surface,border:`1px solid ${G.border}`,borderRadius:G.radius,color:G.text,cursor:"pointer",fontSize:13,display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}}>
                <span style={{fontWeight:600}}>{u.name}</span>
                <span style={{fontSize:10,color:G.muted,letterSpacing:".06em",textTransform:"uppercase"}}>{ROLE_LABELS[u.role]||u.role}</span>
              </button>
            ))}
            {options.length===0 && <div style={{fontSize:13,color:G.muted,padding:"8px 12px",fontStyle:"italic"}}>{search?"No matches.":"Start typing to search Allied staff."}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function TicketCard({ ticket, onView, G }) {
  return (
    <div onClick={()=>onView(ticket)} className="card-hover" style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,padding:"16px 20px",cursor:"pointer",transition:"border-color .18s",borderLeft:`3px solid ${STATUS_COLORS[ticket.status]||G.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
            <span className="mono" style={{fontSize:12,color:G.muted}}>{ticket.id}</span>
            <Badge text={ticket.status} color={STATUS_COLORS[ticket.status]||G.muted} />
            <Badge text={ticket.urgency} color={URGENCY_COLORS[ticket.urgency]||G.muted} />
            {ticket.outsideContractor&&<Badge text="Outside Contractor" color={G.accent} />}
            {ticket.invoiceFileId&&<Badge text="Invoice" color="#60a5fa" />}
          </div>
          <div style={{fontWeight:600,fontSize:16,marginBottom:2,color:G.text}}>{ticket.clientName}</div>
          <div style={{color:G.muted,fontSize:13}}>{ticket.clientAddress||[ticket.clientStreet,ticket.clientCity,ticket.clientState].filter(Boolean).join(", ")}</div>
        </div>
        <div style={{textAlign:"right",flexShrink:0}}>
          <div style={{fontSize:12,color:G.muted}}>{ticket.workType}</div>
          <div style={{fontSize:12,color:G.muted,marginTop:2}}>{fmtDate(ticket.dateReported)}</div>
        </div>
      </div>
      <div style={{color:G.muted,fontSize:13,marginTop:10,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{ticket.description}</div>
    </div>
  );
}

function TicketDetail({ ticket: initTicket, user, onClose, onUpdated, G }) {
  const isMobile = useIsMobile();
  const [ticket, setTicket] = useState(initTicket);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    clientName:initTicket.clientName, clientPhone:initTicket.clientPhone||"",
    clientStreet:initTicket.clientStreet||"", clientUnit:initTicket.clientUnit||"",
    clientCity:initTicket.clientCity||"", clientState:initTicket.clientState||"",
    clientZip:initTicket.clientZip||"", clientAddress:initTicket.clientAddress||"",
    dateReported:initTicket.dateReported, workType:initTicket.workType,
    description:initTicket.description, urgency:initTicket.urgency,
    outsideContractor:initTicket.outsideContractor, status:initTicket.status,
  });
  const [editPhoto, setEditPhoto] = useState(null);
  const [compPhoto, setCompPhoto] = useState(null);
  const [compNote, setCompNote] = useState("");
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [err, setErr] = useState(""); const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [assignContractorId, setAssignContractorId] = useState("");
  const [assignNote, setAssignNote] = useState("");
  const [showAssign, setShowAssign] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(ticket.scheduledDate||"");
  const compPhotoRef = useRef(); const invoiceRef = useRef();

  const isAdmin = user.role==="admin"||user.role==="superadmin";

  // Load contractors for assign dropdown (admin only, outsideContractor tickets)
  useEffect(()=>{
    if (isAdmin && ticket.outsideContractor && !ticket.assignedContractorId) {
      contractorsAPI.list().then(data=>setContractors(Array.isArray(data)?data:[])).catch(()=>{});
    }
  },[isAdmin, ticket.outsideContractor, ticket.assignedContractorId]);

  function flash(m) { setMsg(m); setTimeout(()=>setMsg(""),3000); }

  async function saveEdit(e) {
    e.preventDefault(); setErr(""); setSaving(true);
    try {
      const updated = await ticketsAPI.update(ticket.id,{...editForm,outsideContractor:String(editForm.outsideContractor)},editPhoto);
      setTicket(updated); setEditing(false); setEditPhoto(null); flash("Ticket updated"); onUpdated();
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  async function markComplete(e) {
    e.preventDefault(); setErr(""); setSaving(true);
    try {
      const updated = await ticketsAPI.markComplete(ticket.id,{completionNote:compNote,completionPhoto:compPhoto,invoiceFile});
      setTicket(updated); setCompPhoto(null); setCompNote(""); setInvoiceFile(null);
      if(compPhotoRef.current) compPhotoRef.current.value="";
      if(invoiceRef.current) invoiceRef.current.value="";
      flash("Marked as Work Complete"); onUpdated();
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  async function changeStatus(status) {
    setSaving(true);
    try { const updated=await ticketsAPI.setStatus(ticket.id,status); setTicket(updated); flash("Status: "+status); onUpdated(); }
    catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  async function doAssign() {
    if (!assignContractorId) return;
    setSaving(true); setErr("");
    try {
      const updated = await bidsAPI.assign(ticket.id, assignContractorId, assignNote);
      setTicket(updated); setShowAssign(false); flash("Contractor assigned!"); onUpdated();
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  async function doSchedule() {
    if (!scheduledDate) return;
    setSaving(true); setErr("");
    try {
      const updated = await bidsAPI.schedule(ticket.id, scheduledDate);
      setTicket(updated); flash("Work scheduled for "+scheduledDate); onUpdated();
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  // Match contractors to ticket work type
  function getContractorMatch(c) {
    const profile = c.contractorProfile;
    if (!profile) return "none";
    const services = profile.services||[];
    const workType = ticket.workType;
    if (services.some(s=>s.toLowerCase()===workType.toLowerCase())) return "exact";
    if (services.some(s=>s.toLowerCase().includes(workType.toLowerCase())||workType.toLowerCase().includes(s.toLowerCase().split("/")[0].trim()))) return "partial";
    return "none";
  }

  const canEdit = isAdmin||(user.role==="user"&&ticket.createdBy===user.id);
  const isContractor = user.role==="contractor";
  const isApar = user.role==="ap_ar";
  const workActive = !["Work Complete","Payment Issued","Closed/Completed"].includes(ticket.status);
  const isAssigned = !!ticket.assignedContractorId;
  const isAllied = ["user","ap_ar","admin","superadmin"].includes(user.role);
  const isLocksmithView = user.role==="contractor" && ticket.assignedLocksmithId===user.id;
  const isInvestigate = ticket.workType==="Investigate";
  const showVisitDetails = isAllied && isInvestigate;
  const hideInvoiceUpload = isAllied && isInvestigate;
  const displayAddr = ticket.clientAddress || [ticket.clientStreet,ticket.clientUnit,ticket.clientCity,ticket.clientState,ticket.clientZip].filter(Boolean).join(", ");

  return (
    <Modal title={`Ticket ${ticket.id}`} onClose={onClose} width={720} noConfirm={!editing||["Closed/Completed","Cancelled"].includes(ticket.status)} G={G}>
      <Alert msg={err} G={G} /><Alert msg={msg} type="success" G={G} />
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:20}}>
        <Badge text={ticket.status} color={STATUS_COLORS[ticket.status]||G.muted} />
        <Badge text={ticket.urgency} color={URGENCY_COLORS[ticket.urgency]||G.muted} />
        <Badge text={ticket.workType} color="#60a5fa" />
        {ticket.outsideContractor&&<Badge text="Outside Contractor" color={G.accent} />}
      </div>
      {editing ? (
        <form onSubmit={saveEdit}>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:14}}>
            <Field label="Client Name" required G={G}><input value={editForm.clientName} onChange={e=>setEditForm({...editForm,clientName:e.target.value})} /></Field>
            <Field label="Contact Phone" G={G}><input value={editForm.clientPhone} onChange={e=>setEditForm({...editForm,clientPhone:e.target.value})} placeholder="(555) 555-5555" /></Field>
          </div>
          <div style={{background:G.bg,border:`1px solid ${G.border}`,borderRadius:G.radius,padding:"14px 16px",marginBottom:14}}>
            <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.accent,marginBottom:12}}>Service Address</div>
            <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"2fr 1fr",gap:12}}>
              <Field label="Street" G={G}><input value={editForm.clientStreet} onChange={e=>setEditForm({...editForm,clientStreet:e.target.value})} /></Field>
              <Field label="Unit" G={G}><input value={editForm.clientUnit} onChange={e=>setEditForm({...editForm,clientUnit:e.target.value})} /></Field>
            </div>
            <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"2fr 1fr 1fr",gap:12}}>
              <Field label="City" G={G}><input value={editForm.clientCity} onChange={e=>setEditForm({...editForm,clientCity:e.target.value})} /></Field>
              <Field label="State" G={G}><input value={editForm.clientState} onChange={e=>setEditForm({...editForm,clientState:e.target.value})} /></Field>
              <Field label="Zip" G={G}><input value={editForm.clientZip} onChange={e=>setEditForm({...editForm,clientZip:e.target.value})} /></Field>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:14}}>
            <Field label="Date Reported" required G={G}><input type="date" value={editForm.dateReported} onChange={e=>setEditForm({...editForm,dateReported:e.target.value})} /></Field>
            <Field label="Work Type" G={G}><select value={editForm.workType} onChange={e=>setEditForm({...editForm,workType:e.target.value})}>{WORK_TYPES.map(w=><option key={w}>{w}</option>)}</select></Field>
          </div>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:14}}>
            <Field label="Urgency" G={G}><select value={editForm.urgency} onChange={e=>setEditForm({...editForm,urgency:e.target.value})}>{URGENCY.map(u=><option key={u}>{u}</option>)}</select></Field>
            {isAdmin&&<Field label="Status" G={G}><select value={editForm.status} onChange={e=>setEditForm({...editForm,status:e.target.value})}>{STATUS_TABS.map(s=><option key={s}>{s}</option>)}</select></Field>}
          </div>
          <Field label="Description" G={G}><textarea rows={3} value={editForm.description} onChange={e=>setEditForm({...editForm,description:e.target.value})} style={{resize:"vertical"}} /></Field>
          <Field label="Replace Photo (Optional)" G={G}><input type="file" accept="image/*" onChange={e=>setEditPhoto(e.target.files[0]||null)} /></Field>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
            <input type="checkbox" id="ec" checked={editForm.outsideContractor} onChange={e=>setEditForm({...editForm,outsideContractor:e.target.checked})} style={{width:18,height:18}} />
            <label htmlFor="ec" style={{margin:0,textTransform:"none",fontSize:14,color:G.text,cursor:"pointer"}}>Outside Contractor Needed?</label>
          </div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}><Btn loading={saving} G={G}>Save Changes</Btn><Btn variant="ghost" onClick={()=>setEditing(false)} G={G}>Cancel</Btn></div>
        </form>
      ) : (
        <>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:10,marginBottom:14}}>
            {[["Client",ticket.clientName],["Phone",ticket.clientPhone||"—"],["Date Reported",fmtDate(ticket.dateReported)],["Created By",ticket.createdByName],["Created",fmtDate(ticket.createdAt)]].map(([k,v])=>(
              <div key={k} style={{background:G.bg,borderRadius:G.radius,padding:"10px 14px"}}>
                <div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",marginBottom:3}}>{k}</div>
                <div style={{fontSize:14,fontWeight:500,color:G.text}}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{background:G.bg,borderRadius:G.radius,padding:"10px 14px",marginBottom:12}}>
            <div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",marginBottom:3}}>Service Address</div>
            <div style={{fontSize:14,color:G.text}}>{displayAddr}</div>
            {ticket.clientStreet&&<div style={{fontSize:12,color:G.muted,marginTop:4,display:"flex",gap:12,flexWrap:"wrap"}}>
              {ticket.clientStreet&&<span>Street: {ticket.clientStreet}{ticket.clientUnit?` ${ticket.clientUnit}`:""}</span>}
              {ticket.clientCity&&<span>City: {ticket.clientCity}</span>}
              {ticket.clientState&&<span>State: {ticket.clientState}</span>}
              {ticket.clientZip&&<span>Zip: {ticket.clientZip}</span>}
            </div>}
          </div>
          {ticket.contacts?.length>0&&(
            <div style={{background:G.bg,borderRadius:G.radius,padding:"10px 14px",marginBottom:12}}>
              <div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",marginBottom:8}}>Contacts</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {ticket.contacts.map((c,i)=>(
                  <div key={i} style={{display:"flex",gap:16,flexWrap:"wrap",fontSize:13,color:G.text}}>
                    <span style={{fontWeight:600}}>{c.name}</span>
                    {c.relationship&&<span style={{color:G.muted}}>{c.relationship}</span>}
                    {c.phone&&<span>{c.phone}</span>}
                    {c.email&&<span>{c.email}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
          {isLocksmithView && ticket.contractorContext && (
            <div style={{background:G.accent+"10",border:`1px solid ${G.accent}44`,borderRadius:G.radius,padding:"10px 14px",marginBottom:12}}>
              <div style={{fontSize:11,color:G.accent,textTransform:"uppercase",letterSpacing:".08em",marginBottom:3,fontWeight:700}}>Job Context</div>
              <div style={{fontSize:14,lineHeight:1.6,color:G.text}}>{ticket.contractorContext}</div>
            </div>
          )}
          <div style={{background:G.bg,borderRadius:G.radius,padding:"10px 14px",marginBottom:16}}>
            <div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",marginBottom:3}}>Description</div>
            <div style={{fontSize:14,lineHeight:1.6,color:G.text}}>{ticket.description}</div>
          </div>
          {showVisitDetails && (
            <div style={{marginBottom:16,paddingTop:14,borderTop:`1px solid ${G.border}`}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,color:G.accent,marginBottom:10,letterSpacing:".04em"}}>VISIT DETAILS</div>
              <div style={{background:G.bg,border:`1px solid ${G.border}`,borderRadius:G.radius,padding:"12px 14px",marginBottom:10}}>
                <div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",fontWeight:700,marginBottom:8}}>Key Status</div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                  {KEY_STATUSES.map(k=>(
                    <label key={k} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 12px",background:ticket.keyStatus===k?G.accent+"22":G.surface,border:`1px solid ${ticket.keyStatus===k?G.accent:G.border}`,borderRadius:G.radius,cursor:"pointer",textTransform:"none",fontSize:13,color:G.text,fontWeight:ticket.keyStatus===k?600:400,letterSpacing:"normal",margin:0,flex:"1 1 180px"}}>
                      <input type="radio" name="ticket-keyStatus" value={k} checked={ticket.keyStatus===k} onChange={async()=>{
                        try { const updated=await ticketsAPI.patchFields(ticket.id,{keyStatus:k}); setTicket(updated); onUpdated(); }
                        catch(ex) { setErr(ex.message); }
                      }} />
                      {k}
                    </label>
                  ))}
                </div>
              </div>
              {ticket.keyStatus==="Locksmith Required" && (
                <LocksmithAssignField ticket={ticket} onUpdated={(updated)=>{setTicket(updated);onUpdated();}} G={G} />
              )}
              <AlliedAssigneeField ticket={ticket} onUpdated={(updated)=>{setTicket(updated);onUpdated();}} G={G} />
            </div>
          )}
          {ticket.photoFileId&&(<div style={{marginBottom:16}}><div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",marginBottom:6}}>Submitted Photo</div><img src={fileUrl(ticket.photoFileId,"photos")} alt="Ticket" style={{maxWidth:"100%",maxHeight:240,borderRadius:G.radius,objectFit:"cover",border:`1px solid ${G.border}`}} /></div>)}
          {ticket.completionPhotoFileId&&(<div style={{marginBottom:16}}><div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",marginBottom:6}}>Completion Photo</div><img src={fileUrl(ticket.completionPhotoFileId,"photos")} alt="Completion" style={{maxWidth:"100%",maxHeight:240,borderRadius:G.radius,objectFit:"cover",border:`1px solid ${G.border}`}} />{ticket.completionNote&&<p style={{marginTop:8,fontSize:13,color:G.muted,fontStyle:"italic"}}>{ticket.completionNote}</p>}</div>)}
          {ticket.invoiceFileId&&(isApar||isAdmin)&&(
            <div style={{marginBottom:16,background:G.bg,borderRadius:G.radius,padding:"12px 16px",display:"flex",alignItems:"center",gap:12,border:`1px solid ${G.border}`}}>
              <div style={{flex:1}}><div style={{fontSize:11,color:G.muted,marginBottom:2,textTransform:"uppercase",letterSpacing:".06em"}}>Invoice</div><div style={{fontSize:13,fontWeight:500,color:G.text}}>{ticket.invoiceFilename||"invoice.pdf"}</div></div>
              <Btn size="sm" onClick={()=>ticketsAPI.downloadInvoice(ticket.invoiceFileId,ticket.invoiceFilename)} G={G}>Download</Btn>
            </div>
          )}
          {/* Assigned Contractor Panel */}
          {isAssigned&&(
            <div style={{background:G.accent+"18",border:`1px solid ${G.accent}44`,borderRadius:G.radius,padding:"12px 16px",marginBottom:12}}>
              <div style={{fontSize:11,color:G.accent,textTransform:"uppercase",letterSpacing:".08em",marginBottom:6,fontWeight:700}}>Assigned Contractor</div>
              <div style={{fontWeight:700,fontSize:15,color:G.text}}>{ticket.assignedContractorName}</div>
              {ticket.assignedContractorCompany&&<div style={{fontSize:13,color:G.muted}}>{ticket.assignedContractorCompany}</div>}
              {ticket.assignedContractorPhone&&<div style={{fontSize:13,color:G.muted}}>{ticket.assignedContractorPhone}</div>}
              {ticket.scheduledDate&&<div style={{fontSize:13,color:G.accent,marginTop:4,fontWeight:600}}>Work Scheduled: {fmtDate(ticket.scheduledDate)}</div>}
            </div>
          )}
          {/* Schedule Date — shown when assigned but not yet scheduled */}
          {isAdmin&&isAssigned&&!ticket.scheduledDate&&(
            <div style={{background:G.bg,border:`1px solid ${G.border}`,borderRadius:G.radius,padding:"12px 16px",marginBottom:12}}>
              <div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",marginBottom:8}}>Set Work Scheduled Date</div>
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <input type="date" value={scheduledDate} onChange={e=>setScheduledDate(e.target.value)} style={{width:"auto",flex:1}} />
                <Btn size="sm" onClick={doSchedule} loading={saving} G={G}>Set Schedule</Btn>
              </div>
            </div>
          )}
          {/* Direct Assign Panel — admin only, outsideContractor, not yet assigned */}
          {isAdmin&&ticket.outsideContractor&&!isAssigned&&ticket.status==="Open"&&(
            <div style={{marginBottom:12}}>
              <Btn size="sm" variant="secondary" onClick={()=>setShowAssign(v=>!v)} G={G}>
                {showAssign?"Cancel":"Assign Contractor Directly"}
              </Btn>
              {showAssign&&contractors.length>0&&(
                <div style={{background:G.bg,border:`1px solid ${G.border}`,borderRadius:G.radius,padding:"14px 16px",marginTop:8}}>
                  <div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",marginBottom:10}}>Select Contractor</div>
                  <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:12,maxHeight:220,overflowY:"auto"}}>
                    {[...contractors].sort((a,b)=>{
                      const order = {exact:0,partial:1,none:2};
                      return order[getContractorMatch(a)]-order[getContractorMatch(b)];
                    }).map(c=>{
                      const match = getContractorMatch(c);
                      const matchColor = match==="exact"?G.success:match==="partial"?"#f59e0b":G.muted;
                      const matchLabel = match==="exact"?"✓ Compatible":match==="partial"?"~ Related":"No match";
                      return (
                        <label key={c.id} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:assignContractorId===c.id?G.accent+"18":G.surface,border:`1px solid ${assignContractorId===c.id?G.accent:G.border}`,borderRadius:G.radius,cursor:"pointer",margin:0}}>
                          <input type="radio" name="assignContractor" value={c.id} checked={assignContractorId===c.id} onChange={()=>setAssignContractorId(c.id)} style={{flexShrink:0}} />
                          <div style={{flex:1}}>
                            <div style={{fontWeight:600,color:G.text,fontSize:13}}>{c.name}</div>
                            {c.contractorProfile?.companyName&&<div style={{fontSize:12,color:G.muted}}>{c.contractorProfile.companyName}</div>}
                          </div>
                          <span style={{fontSize:11,color:matchColor,fontWeight:600,whiteSpace:"nowrap"}}>{matchLabel}</span>
                        </label>
                      );
                    })}
                  </div>
                  <Field label="Note to Contractor (Optional)" G={G}>
                    <input value={assignNote} onChange={e=>setAssignNote(e.target.value)} placeholder="Any instructions or context..." />
                  </Field>
                  <Btn size="sm" onClick={doAssign} loading={saving} disabled={!assignContractorId} G={G}>Assign Contractor</Btn>
                </div>
              )}
              {showAssign&&contractors.length===0&&(
                <div style={{color:G.muted,fontSize:13,marginTop:8,padding:"10px 12px",background:G.bg,borderRadius:G.radius,border:`1px solid ${G.border}`}}>No approved contractors found.</div>
              )}
            </div>
          )}
          <div style={{display:"flex",gap:10,flexWrap:"wrap",marginTop:4}}>
            {canEdit&&<Btn variant="secondary" size="sm" onClick={()=>setEditing(true)} G={G}>Edit</Btn>}
            {isAdmin&&ticket.status==="New"&&<Btn size="sm" onClick={()=>changeStatus("Open")} loading={saving} G={G}>Mark Open</Btn>}
            {isAdmin&&ticket.status==="Assigned to Contractor"&&!ticket.scheduledDate&&<Btn size="sm" onClick={()=>changeStatus("Work Scheduled")} loading={saving} G={G}>Mark Work Scheduled</Btn>}
            {isApar&&ticket.status==="Work Complete"&&<Btn size="sm" variant="success" onClick={()=>changeStatus("Payment Issued")} loading={saving} G={G}>Issue Payment</Btn>}
            {isApar&&ticket.status==="Payment Issued"&&<Btn size="sm" variant="ghost" onClick={()=>changeStatus("Closed/Completed")} loading={saving} G={G}>Close Ticket</Btn>}
            {isAdmin&&!["Closed/Completed","Cancelled"].includes(ticket.status)&&(
              <Btn size="sm" variant={isAssigned?"ghost":"danger"} onClick={()=>changeStatus(isAssigned?"Closed/Completed":"Cancelled")} loading={saving} G={G}>
                {isAssigned?"Close Ticket":"Cancel Ticket"}
              </Btn>
            )}
          </div>
          {(isContractor||isAdmin)&&workActive&&(
            <div style={{marginTop:24,borderTop:`1px solid ${G.border}`,paddingTop:20}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:18,marginBottom:14,color:G.accent}}>Mark Work Complete</div>
              <form onSubmit={markComplete}>
                <Field label="Completion Photo" required G={G}><input ref={compPhotoRef} type="file" accept="image/*" onChange={e=>setCompPhoto(e.target.files[0]||null)} /></Field>
                <Field label="Completion Notes" G={G}><textarea rows={2} value={compNote} onChange={e=>setCompNote(e.target.value)} placeholder="Notes about the completed work..." style={{resize:"vertical"}} /></Field>
                {!hideInvoiceUpload && <Field label="Upload Invoice (PDF or Image, Optional)" G={G}><input ref={invoiceRef} type="file" accept=".pdf,application/pdf,image/*" onChange={e=>setInvoiceFile(e.target.files[0]||null)} /></Field>}
                <Btn variant="success" loading={saving} G={G}>Submit Completion</Btn>
              </form>
            </div>
          )}
        </>
      )}
          {/* ── Bids Panel (admin only) ── */}
          {isAdmin&&ticket.outsideContractor&&(ticket.bids?.length>0)&&(
            <div style={{marginTop:24,borderTop:`1px solid ${G.border}`,paddingTop:20}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:18,marginBottom:14,color:G.accent}}>Bids ({ticket.bids.filter(b=>!["withdrawn","declined"].includes(b.status)).length} active)</div>
              {ticket.bids.map(bid=>(
                <BidRow key={bid.id} bid={bid} ticket={ticket} G={G} onUpdated={async()=>{
                  const fresh=(await ticketsAPI.list()).find(t=>t.id===ticket.id);
                  if(fresh){setTicket(fresh);onUpdated();}
                }} />
              ))}
            </div>
          )}
          {/* ── Q&A Panel (admin only) ── */}
          {isAdmin&&ticket.outsideContractor&&(ticket.questions?.length>0)&&(
            <div style={{marginTop:24,borderTop:`1px solid ${G.border}`,paddingTop:20}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:18,marginBottom:14,color:G.accent}}>Contractor Questions ({ticket.questions.length})</div>
              {ticket.questions.map(q=>(
                <QAThread key={q.id} q={q} ticket={ticket} G={G} onUpdated={async()=>{
                  const fresh=(await ticketsAPI.list()).find(t=>t.id===ticket.id);
                  if(fresh){setTicket(fresh);onUpdated();}
                }} />
              ))}
            </div>
          )}
      {ticket.history?.length>0&&(
        <div style={{marginTop:24,borderTop:`1px solid ${G.border}`,paddingTop:16}}>
          <div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",marginBottom:10}}>Activity Log</div>
          {[...ticket.history].reverse().map((h,i)=>(
            <div key={i} style={{display:"flex",gap:10,fontSize:12,marginBottom:6,alignItems:"flex-start"}}>
              <span className="mono" style={{color:G.muted,flexShrink:0,fontSize:11}}>{new Date(h.at).toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}</span>
              <span style={{color:G.border}}>|</span>
              <span style={{color:G.text,flex:1}}>{h.msg}</span>
              <span style={{color:G.accentDim,fontSize:11,flexShrink:0}}>-- {h.by}</span>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ── Contractor Dashboard ─────────────────────────────────────────────────────
function ContractorDashboard({ user, G }) {
  const isMobile = useIsMobile();
  const [allTickets, setAllTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [section, setSection] = useState("open"); // "open" | "mine"

  async function load() {
    setLoading(true);
    try { setAllTickets(await ticketsAPI.list()); }
    catch(ex) { console.error(ex); }
    finally { setLoading(false); }
  }
  useEffect(()=>{ load(); },[]);

  // My Jobs: tickets assigned to this contractor (as outsideContractor or as locksmith)
  const myJobs = allTickets.filter(t => t.assignedContractorId === user.id || t.assignedLocksmithId === user.id);
  // Open Jobs: status "Open", outsideContractor=true, not assigned to anyone
  const openJobs = allTickets.filter(t => t.status==="Open" && t.outsideContractor && !t.assignedContractorId);

  function getBidBadge(ticket) {
    const hasBids = (ticket.bids||[]).filter(b=>!["withdrawn","declined"].includes(b.status)).length > 0;
    const hasQs = (ticket.questions||[]).length > 0;
    if (hasBids && hasQs) return { text:"High Interest", color:"#a855f7" };
    if (hasBids) return { text:"Bid(s) Submitted", color:"#f59e0b" };
    if (hasQs) return { text:"Question(s) Asked", color:"#60a5fa" };
    return null;
  }

  function getMyBid(ticket) {
    return (ticket.bids||[]).find(b => b.contractorId===user.id && b.status!=="withdrawn");
  }

  const tabs = [{id:"open",label:`Open Jobs (${openJobs.length})`},{id:"mine",label:`My Jobs (${myJobs.length})`}];

  return (
    <div style={{maxWidth:1000,margin:"0 auto",padding:isMobile?"12px":"32px 16px"}} className="page-enter">
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:isMobile?26:34,letterSpacing:".04em",marginBottom:20,color:G.text}}>
        <span style={{color:G.accent}}>//</span> CONTRACTOR DASHBOARD
      </div>
      <div style={{display:"flex",gap:0,marginBottom:24,borderBottom:`1px solid ${G.border}`}}>
        {tabs.map(t=>(
          <button key={t.id} onClick={()=>setSection(t.id)} style={{padding:"10px 18px",background:"none",border:"none",borderBottom:`3px solid ${section===t.id?G.accent:"transparent"}`,color:section===t.id?G.accent:G.muted,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:".06em",cursor:"pointer",whiteSpace:"nowrap"}}>
            {t.label}
          </button>
        ))}
      </div>
      {loading ? <Spinner G={G} /> : (
        <>
          {section==="open" && (
            openJobs.length===0
              ? <div style={{textAlign:"center",padding:"60px 0",color:G.muted}}><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20}}>No open jobs right now</div></div>
              : <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {openJobs.map(t=>(
                    <OpenJobCard key={t.id} ticket={t} user={user} G={G}
                      badge={getBidBadge(t)} myBid={getMyBid(t)}
                      onView={()=>setViewing(t)} onRefresh={load} />
                  ))}
                </div>
          )}
          {section==="mine" && (
            myJobs.length===0
              ? <div style={{textAlign:"center",padding:"60px 0",color:G.muted}}><div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20}}>No assigned jobs yet</div></div>
              : <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {myJobs.map(t=>(
                    <div key={t.id} onClick={()=>setViewing(t)} className="card-hover"
                      style={{background:G.surface,border:`1px solid ${G.border}`,borderLeft:`3px solid ${STATUS_COLORS[t.status]||G.accent}`,borderRadius:8,padding:"16px 20px",cursor:"pointer"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
                        <div>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                            <span className="mono" style={{fontSize:12,color:G.muted}}>{t.id}</span>
                            <Badge text={t.status} color={STATUS_COLORS[t.status]||G.muted} />
                            <Badge text={t.urgency} color={URGENCY_COLORS[t.urgency]||G.muted} />
                          </div>
                          <div style={{fontWeight:600,fontSize:16,color:G.text}}>{t.clientName}</div>
                          <div style={{color:G.muted,fontSize:13}}>{t.clientAddress}</div>
                        </div>
                        <div style={{textAlign:"right",flexShrink:0}}>
                          <div style={{fontSize:12,color:G.muted}}>{t.workType}</div>
                          <div style={{fontSize:12,color:G.muted,marginTop:2}}>{fmtDate(t.dateReported)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
          )}
        </>
      )}
      {viewing&&(
        <TicketDetail ticket={viewing} user={user} G={G} onClose={()=>setViewing(null)}
          onUpdated={async()=>{ await load(); const fresh=allTickets.find(t=>t.id===viewing.id); if(fresh)setViewing(fresh); }}
        />
      )}
    </div>
  );
}

function OpenJobCard({ ticket, user, G, badge, myBid, onView, onRefresh }) {
  const isMobile = useIsMobile();
  const [showBidForm, setShowBidForm] = useState(false);
  const [showQForm, setShowQForm] = useState(false);
  const [bidAmount, setBidAmount] = useState(myBid?String(myBid.amount):"");
  const [bidNote, setBidNote] = useState(myBid?.note||"");
  const [qMessage, setQMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const activeBids = (ticket.bids||[]).filter(b=>!["withdrawn","declined"].includes(b.status));
  const totalBids = activeBids.length;
  const lowestBid = totalBids > 0 ? Math.min(...activeBids.map(b=>b.amount)) : null;

  async function submitBid(e) {
    e.preventDefault(); setErr(""); setSaving(true);
    try {
      if (myBid) { await bidsAPI.update(ticket.id, myBid.id, { amount:parseFloat(bidAmount), note:bidNote }); }
      else { await bidsAPI.submit(ticket.id, { amount:parseFloat(bidAmount), note:bidNote }); }
      setShowBidForm(false); onRefresh();
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  async function withdrawBid() {
    setSaving(true);
    try { await bidsAPI.withdraw(ticket.id, myBid.id); onRefresh(); }
    catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  async function submitQuestion(e) {
    e.preventDefault(); setErr(""); setSaving(true);
    try {
      await questionsAPI.ask(ticket.id, qMessage);
      setQMessage(""); setShowQForm(false); onRefresh();
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,overflow:"hidden"}}>
      <div style={{padding:"16px 20px",borderBottom:showBidForm||showQForm?`1px solid ${G.border}`:"none"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
          <div style={{flex:1}}>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
              <span className="mono" style={{fontSize:12,color:G.muted}}>{ticket.id}</span>
              <Badge text={ticket.urgency} color={URGENCY_COLORS[ticket.urgency]||G.muted} />
              <Badge text={ticket.workType} color="#60a5fa" />
              {badge&&<Badge text={badge.text} color={badge.color} />}
              {myBid&&<Badge text="Bid Pending Review" color="#f59e0b" />}
            </div>
            <div style={{fontWeight:600,fontSize:16,color:G.text,marginBottom:2}}>{ticket.clientName}</div>
            <div style={{color:G.muted,fontSize:13,marginBottom:6}}>{ticket.clientAddress}</div>
            <div style={{fontSize:12,color:G.muted}}>{fmtDate(ticket.dateReported)} · {ticket.description?.slice(0,80)}{ticket.description?.length>80?"...":""}</div>
            {totalBids>0&&(
              <div style={{marginTop:8,fontSize:12,color:G.muted}}>
                <span style={{color:G.text,fontWeight:600}}>{totalBids}</span> bid{totalBids!==1?"s":""} submitted
                {lowestBid!==null&&<span> · Lowest: <span style={{color:G.accent,fontWeight:600}}>${lowestBid.toLocaleString()}</span></span>}
                {myBid&&<span> · Your bid: <span style={{color:G.accent,fontWeight:600}}>${myBid.amount.toLocaleString()}</span></span>}
              </div>
            )}
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            <Btn size="sm" variant="secondary" onClick={onView} G={G}>View Details</Btn>
            {myBid ? (
              <>
                <Btn size="sm" onClick={()=>{setShowBidForm(v=>!v);setShowQForm(false);}} G={G}>{showBidForm?"Cancel":"Edit Bid"}</Btn>
                <Btn size="sm" variant="danger" onClick={withdrawBid} loading={saving} G={G}>Withdraw</Btn>
              </>
            ) : (
              <Btn size="sm" onClick={()=>{setShowBidForm(v=>!v);setShowQForm(false);setErr("");}} G={G}>{showBidForm?"Cancel":"Submit Bid"}</Btn>
            )}
            <Btn size="sm" variant="ghost" onClick={()=>{setShowQForm(v=>!v);setShowBidForm(false);setErr("");}} G={G}>{showQForm?"Cancel":"Ask a Question"}</Btn>
          </div>
        </div>
        {err&&<Alert msg={err} G={G} />}
      </div>
      {showBidForm&&(
        <form onSubmit={submitBid} style={{padding:"16px 20px",background:G.bg,borderTop:`1px solid ${G.border}`}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:16,marginBottom:12,color:G.accent}}>{myBid?"Update Your Bid":"Submit a Bid"}</div>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 2fr",gap:12}}>
            <Field label="Bid Amount ($)" required G={G}><input type="number" min="0" step="0.01" value={bidAmount} onChange={e=>setBidAmount(e.target.value)} placeholder="e.g. 1500" /></Field>
            <Field label="Note (Optional)" G={G}><input value={bidNote} onChange={e=>setBidNote(e.target.value)} placeholder="Brief description of your approach..." /></Field>
          </div>
          <div style={{display:"flex",gap:10,marginTop:4}}>
            <Btn loading={saving} G={G}>{myBid?"Update Bid":"Submit Bid"}</Btn>
            <Btn variant="ghost" onClick={()=>setShowBidForm(false)} G={G}>Cancel</Btn>
          </div>
        </form>
      )}
      {showQForm&&(
        <form onSubmit={submitQuestion} style={{padding:"16px 20px",background:G.bg,borderTop:`1px solid ${G.border}`}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:16,marginBottom:12,color:G.accent}}>Ask a Question</div>
          <Field label="Your Question" required G={G}><textarea rows={2} value={qMessage} onChange={e=>setQMessage(e.target.value)} placeholder="What would you like to know about this job?" style={{resize:"vertical"}} /></Field>
          <div style={{display:"flex",gap:10}}>
            <Btn loading={saving} G={G}>Send Question</Btn>
            <Btn variant="ghost" onClick={()=>setShowQForm(false)} G={G}>Cancel</Btn>
          </div>
        </form>
      )}
    </div>
  );
}

// ── Tickets Page ──────────────────────────────────────────────────────────────
function TicketsPage({ user, refresh, onNewTicket, initialTicket, clearInitialTicket, G }) {
  const isMobile = useIsMobile();
  const isAdmin = user.role==="admin"||user.role==="superadmin";
  const canCreate = user.role==="user"||isAdmin;
  const [tab, setTab] = useState("New");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(initialTicket||null);
  const [search, setSearch] = useState("");
  const [counts, setCounts] = useState({});

  // initialTicket is one-shot: seed `viewing` once on mount, then clear the
  // App-level state so navigating away and back doesn't reopen the modal.
  useEffect(() => {
    if (initialTicket) clearInitialTicket();
  }, [initialTicket]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const all = await ticketsAPI.list();
      const c = {};
      STATUS_TABS.forEach(s => { c[s] = all.filter(t=>t.status===s).length; });
      setCounts(c);
      setTickets(all.filter(t=>t.status===tab));
    } catch(ex) { console.error(ex); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { loadTickets(); }, [loadTickets, refresh]);

  const filtered = search ? tickets.filter(t => {
    const q = search.toLowerCase();
    return t.clientName.toLowerCase().includes(q)||(t.clientAddress||"").toLowerCase().includes(q)||t.id.toLowerCase().includes(q)||t.workType.toLowerCase().includes(q);
  }) : tickets;

  return (
    <div style={{maxWidth:1000,margin:"0 auto",padding:isMobile?"12px":"32px 16px"}} className="page-enter">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,gap:12,flexWrap:"wrap"}}>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:isMobile?26:34,letterSpacing:".04em",color:G.text}}><span style={{color:G.accent}}>//</span> TICKETS</div>
        <div style={{display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search tickets..." style={{width:isMobile?"100%":240}} />
          <Btn variant="ghost" onClick={loadTickets} G={G} title="Refresh">↻</Btn>
          {canCreate&&<Btn onClick={onNewTicket} G={G}>+ New Ticket</Btn>}
        </div>
      </div>
      <div style={{display:"flex",gap:0,marginBottom:24,borderBottom:`1px solid ${G.border}`,overflowX:"auto"}}>
        {STATUS_TABS.map(s=>(
          <button key={s} onClick={()=>setTab(s)} style={{padding:"10px 18px",background:"none",border:"none",borderBottom:`3px solid ${tab===s?STATUS_COLORS[s]:"transparent"}`,color:tab===s?STATUS_COLORS[s]:G.muted,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:".06em",cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
            {s} <span style={{marginLeft:4,background:tab===s?STATUS_COLORS[s]+"33":"transparent",borderRadius:10,padding:"1px 7px",fontSize:12,color:tab===s?STATUS_COLORS[s]:G.muted}}>{counts[s]||0}</span>
          </button>
        ))}
      </div>
      {loading ? <Spinner G={G} /> : filtered.length===0 ? (
        <div style={{textAlign:"center",padding:"60px 0",color:G.muted}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20}}>No tickets here</div>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          {filtered.map(t=><TicketCard key={t.id} ticket={t} onView={t=>setViewing(t)} G={G} />)}
        </div>
      )}
      {viewing&&(
        <TicketDetail ticket={viewing} user={user} G={G} onClose={()=>setViewing(null)}
          onUpdated={async()=>{ await loadTickets(); const fresh=(await ticketsAPI.list()).find(t=>t.id===viewing.id); if(fresh) setViewing(fresh); }}
        />
      )}
    </div>
  );
}

// ── Applications Panel ────────────────────────────────────────────────────────
function ApplicationsPanel({ G }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [requestInfoModal, setRequestInfoModal] = useState(null);
  const [notes, setNotes] = useState("");
  const [activateProfile, setActivateProfile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  function flash(m) { setMsg(m); setTimeout(()=>setMsg(""),3000); }
  async function load() { setLoading(true); try { setApps(await applicationsAPI.list()); } finally { setLoading(false); } }
  useEffect(()=>{ load(); },[]);

  async function approve(id) {
    setSaving(true);
    try { await applicationsAPI.approve(id); flash("Application approved - contractor account activated!"); await load(); setViewing(null); }
    catch(ex) { flash(ex.message); }
    finally { setSaving(false); }
  }
  async function sendRequestInfo() {
    if (!notes.trim()) return; setSaving(true);
    try {
      await applicationsAPI.requestInfo(requestInfoModal.id, notes, activateProfile);
      flash(activateProfile?"Profile activated with info request sent.":"Info request sent - application remains pending.");
      setRequestInfoModal(null); setNotes(""); setActivateProfile(false); await load();
    } catch(ex) { flash(ex.message); }
    finally { setSaving(false); }
  }

  const STATUS_APP_COLORS = { pending:"#f59e0b",approved:"#10b981",needs_info:"#ef4444",approved_needs_info:"#f5a623" };
  const pending = apps.filter(a=>["pending","needs_info"].includes(a.status));
  const reviewed = apps.filter(a=>["approved","approved_needs_info"].includes(a.status));
  if (loading) return <Spinner G={G} />;

  return (
    <div>
      <Alert msg={msg} type="success" G={G} />
      {pending.length===0&&reviewed.length===0&&(
        <div style={{textAlign:"center",padding:"40px 0",color:G.muted}}><div>No contractor applications yet</div></div>
      )}
      {pending.length>0&&(
        <div style={{marginBottom:24}}>
          <div style={{fontSize:12,color:G.accent,textTransform:"uppercase",letterSpacing:".08em",marginBottom:12,fontWeight:700}}>Pending Review ({pending.length})</div>
          {pending.map(a=>(
            <div key={a.id} style={{background:G.bg,border:`1px solid ${G.border}`,borderLeft:`3px solid ${STATUS_APP_COLORS[a.status]}`,borderRadius:G.radius,padding:"14px 16px",marginBottom:8,display:"flex",alignItems:"center",gap:12}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <span style={{fontWeight:600,color:G.text}}>{a.name}</span>
                  {a.companyName&&<span style={{color:G.muted,fontSize:13}}> - {a.companyName}</span>}
                  <Badge text={a.status==="needs_info"?"Needs Info":"Pending"} color={STATUS_APP_COLORS[a.status]} />
                </div>
                <div style={{fontSize:12,color:G.muted}}>{a.email} - Submitted {fmtDate(a.submittedAt)}</div>
              </div>
              <div style={{display:"flex",gap:8}}>
                <Btn size="sm" variant="secondary" onClick={()=>setViewing(a)} G={G}>Review</Btn>
                <Btn size="sm" variant="success" onClick={()=>approve(a.id)} loading={saving} G={G}>Approve</Btn>
                <Btn size="sm" variant="ghost" onClick={()=>{setRequestInfoModal(a);setNotes(a.adminNotes||"");}} G={G}>Request Info</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
      {reviewed.length>0&&(
        <div>
          <div style={{fontSize:12,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",marginBottom:12,fontWeight:700}}>Previously Reviewed ({reviewed.length})</div>
          {reviewed.map(a=>(
            <div key={a.id} style={{background:G.bg,border:`1px solid ${G.border}`,borderLeft:`3px solid ${STATUS_APP_COLORS[a.status]}`,borderRadius:G.radius,padding:"12px 16px",marginBottom:8,display:"flex",alignItems:"center",gap:12,opacity:.8}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontWeight:600,color:G.text}}>{a.name}</span>
                  {a.companyName&&<span style={{color:G.muted,fontSize:13}}> - {a.companyName}</span>}
                  <Badge text={a.status==="approved"?"Approved":"Active + Needs Info"} color={STATUS_APP_COLORS[a.status]} />
                </div>
                <div style={{fontSize:12,color:G.muted}}>{a.email} - Reviewed by {a.reviewedBy} on {fmtDate(a.reviewedAt)}</div>
              </div>
              <Btn size="sm" variant="ghost" onClick={()=>setViewing(a)} G={G}>View</Btn>
            </div>
          ))}
        </div>
      )}
      {viewing&&(
        <Modal title={`Application - ${viewing.name}`} onClose={()=>setViewing(null)} width={680} noConfirm G={G}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
            {[["Name",viewing.name],["Company",viewing.companyName||"-"],["Email",viewing.email],["Phone",viewing.phone||"-"],["Submitted",fmtDate(viewing.submittedAt)],["Status",viewing.status]].map(([k,v])=>(
              <div key={k} style={{background:G.bg,borderRadius:G.radius,padding:"10px 14px"}}>
                <div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",marginBottom:3}}>{k}</div>
                <div style={{fontSize:13,fontWeight:500,color:G.text}}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{background:G.bg,borderRadius:G.radius,padding:"10px 14px",marginBottom:12}}>
            <div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",marginBottom:3}}>Billing Address</div>
            <div style={{fontSize:13,color:G.text}}>{viewing.billingStreet}{viewing.billingUnit?` ${viewing.billingUnit}`:""}, {viewing.billingCity}, {viewing.billingState} {viewing.billingZip}</div>
          </div>
          <div style={{background:G.bg,borderRadius:G.radius,padding:"10px 14px",marginBottom:12}}>
            <div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",marginBottom:6}}>Services Offered</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>{(viewing.services||[]).map(s=><Badge key={s} text={s} color="#60a5fa" />)}</div>
          </div>
          {viewing.licenses?.filter(l=>l.licensed).length>0&&(
            <div style={{background:G.bg,borderRadius:G.radius,padding:"10px 14px",marginBottom:12}}>
              <div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".08em",marginBottom:8}}>Licenses</div>
              {viewing.licenses.filter(l=>l.licensed).map(l=>(
                <div key={l.trade} style={{marginBottom:8}}>
                  <div style={{fontSize:13,fontWeight:600,marginBottom:4,color:G.text}}>{l.trade}</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:4}}>{l.jurisdictions.map(j=><Badge key={j} text={j} color={G.accent} />)}</div>
                </div>
              ))}
            </div>
          )}
          {viewing.adminNotes&&(
            <div style={{background:G.accent+"18",border:`1px solid ${G.accent}44`,borderRadius:G.radius,padding:"10px 14px",marginBottom:12}}>
              <div style={{fontSize:11,color:G.accent,textTransform:"uppercase",letterSpacing:".08em",marginBottom:4}}>Admin Notes</div>
              <div style={{fontSize:13,color:G.text}}>{viewing.adminNotes}</div>
            </div>
          )}
          {["pending","needs_info"].includes(viewing.status)&&(
            <div style={{display:"flex",gap:10,marginTop:16}}>
              <Btn variant="success" onClick={()=>approve(viewing.id)} loading={saving} G={G}>Approve</Btn>
              <Btn variant="ghost" onClick={()=>{setRequestInfoModal(viewing);setNotes(viewing.adminNotes||"");setViewing(null);}} G={G}>Request More Info</Btn>
            </div>
          )}
        </Modal>
      )}
      {requestInfoModal&&(
        <Modal title="Request More Information" onClose={()=>{setRequestInfoModal(null);setNotes("");setActivateProfile(false);}} G={G}>
          <p style={{color:G.muted,fontSize:13,marginBottom:16}}>Describe what information or corrections are needed from <strong style={{color:G.text}}>{requestInfoModal.name}</strong>.</p>
          <Field label="Notes for contractor" required G={G}><textarea rows={4} value={notes} onChange={e=>setNotes(e.target.value)} placeholder="e.g. Please provide proof of General Contractor license for Nassau County..." style={{resize:"vertical"}} /></Field>
          <p style={{color:G.muted,fontSize:13,marginBottom:10}}>How would you like to proceed?</p>
          <div style={{display:"flex",gap:10}}>
            <Btn style={{flex:1}} variant="success" onClick={()=>{if(notes.trim()){setActivateProfile(true);sendRequestInfo();}}} loading={saving} G={G}>Activate + Request Info</Btn>
            <Btn style={{flex:1}} variant="danger" onClick={()=>{if(notes.trim()){setActivateProfile(false);sendRequestInfo();}}} loading={saving} G={G}>Keep Pending + Request Info</Btn>
            <Btn variant="ghost" onClick={()=>{setRequestInfoModal(null);setNotes("");setActivateProfile(false);}} G={G}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── System Panel (superadmin-only master switches) ──────────────────────────
function SystemPanel({ G, config, onUpdate, flash }) {
  const [busy, setBusy] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // "disable" | "enable" | null
  if (!config) return <div style={{padding:40,textAlign:"center",color:G.muted}}>Loading system config...</div>;
  async function applyToggle(newValue) {
    setBusy(true);
    try {
      const updated = await systemConfigAPI.update(newValue);
      onUpdate(updated);
      flash(`Email notifications ${updated.emailsEnabled?"RESUMED":"SUSPENDED"}.`);
    } catch(ex) { flash(ex.message,"error"); }
    finally { setBusy(false); setConfirmAction(null); }
  }
  const enabled = config.emailsEnabled;
  const recentHistory = (config.history||[]).slice(-10).reverse();
  return (
    <div style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,overflow:"hidden"}}>
      <div style={{padding:"16px 20px",borderBottom:`1px solid ${G.border}`}}>
        <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:20,letterSpacing:".04em",color:G.text}}>SYSTEM SETTINGS</span>
      </div>
      <div style={{padding:"20px"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:16,flexWrap:"wrap"}}>
          <div>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:16,letterSpacing:".04em",color:G.text,marginBottom:4}}>EMAIL NOTIFICATIONS</div>
            <div style={{fontSize:13,color:G.muted,marginBottom:8,maxWidth:480}}>Master switch for ALL outbound emails sent by PropTrack (ticket creation, contractor application updates, bid notifications, etc.).</div>
            <div style={{fontSize:13,fontWeight:600,color:enabled?"#22c55e":"#ef4444",letterSpacing:".04em"}}>
              {enabled ? "● ENABLED — Emails are being sent normally" : "● SUSPENDED — No emails are being sent"}
            </div>
          </div>
          <Btn size="md" variant={enabled?"danger":"primary"} disabled={busy} onClick={()=>setConfirmAction(enabled?"disable":"enable")} G={G}>
            {enabled ? "Suspend Email Notifications" : "Resume Email Notifications"}
          </Btn>
        </div>
        {recentHistory.length>0 && (
          <div style={{marginTop:24,paddingTop:20,borderTop:`1px solid ${G.border}`}}>
            <div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".06em",marginBottom:8}}>Recent activity</div>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              {recentHistory.map((h,i)=>(
                <div key={i} style={{fontSize:12,color:G.muted}}>
                  <span style={{color:h.action==="enabled"?"#22c55e":"#ef4444",fontWeight:600}}>{(h.action||"").toUpperCase()}</span>
                  {" "}by {h.by} — <span style={{fontFamily:"monospace"}}>{new Date(h.at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {confirmAction && (
        <Modal title={confirmAction==="disable"?"Suspend Email Notifications?":"Resume Email Notifications?"} onClose={()=>setConfirmAction(null)} noConfirm G={G}>
          <div style={{color:G.text,marginBottom:20,fontSize:14,lineHeight:1.5}}>
            {confirmAction==="disable"
              ? "All outbound emails will stop until you re-enable them. Ticket creation notifications, contractor application updates, bid notifications, and other automated emails will be silently suppressed."
              : "Email notifications will resume. All future emails will be sent normally."
            }
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
            <Btn size="md" variant="ghost" onClick={()=>setConfirmAction(null)} disabled={busy} G={G}>Cancel</Btn>
            <Btn size="md" variant={confirmAction==="disable"?"danger":"primary"} onClick={()=>applyToggle(confirmAction==="enable")} disabled={busy} G={G}>
              {busy ? "..." : (confirmAction==="disable" ? "Suspend" : "Resume")}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Admin Page ────────────────────────────────────────────────────────────────
function AdminPage({ currentUser, G }) {
  const isMobile = useIsMobile();
  const [adminTab, setAdminTab] = useState("users");
  const [users, setUsers] = useState([]); const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(""); const [msgType, setMsgType] = useState("success");
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name:"",email:"",password:"",role:"user" });
  const [creating, setCreating] = useState(false); const [createErr, setCreateErr] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [systemConfig, setSystemConfig] = useState(null);

  const isSuperadmin = currentUser.role === "superadmin";

  function flash(m,t="success") { setMsg(m); setMsgType(t); setTimeout(()=>setMsg(""),3000); }
  function loadData() { Promise.all([usersAPI.list(),statsAPI.get()]).then(([u,s])=>{ setUsers(u); setStats(s); }).finally(()=>setLoading(false)); }
  useEffect(()=>{ loadData(); },[]);
  useEffect(()=>{ if (isSuperadmin) systemConfigAPI.get().then(setSystemConfig).catch(ex=>console.warn("Failed to load system config:",ex.message)); },[isSuperadmin]);

  async function changeRole(uid,role) {
    try { const upd=await usersAPI.setRole(uid,role); setUsers(u=>u.map(x=>x.id===uid?upd:x)); flash("Role updated."); }
    catch(ex) { flash(ex.message,"error"); }
  }
  async function toggleActive(u) {
    try {
      const upd=u.active===false?await usersAPI.activate(u.id):await usersAPI.deactivate(u.id);
      setUsers(us=>us.map(x=>x.id===u.id?upd:x));
      flash(upd.active===false?"User deactivated.":"User activated.");
    } catch(ex) { flash(ex.message,"error"); }
  }
  async function permanentDelete(uid) {
    try {
      await usersAPI.permanentDelete(uid);
      setUsers(us=>us.filter(x=>x.id!==uid));
      flash("User permanently deleted and archived.");
      setConfirmDelete(null);
    } catch(ex) { flash(ex.message,"error"); setConfirmDelete(null); }
  }
  async function handleCreate(e) {
    e.preventDefault(); setCreateErr(""); setCreating(true);
    try { const newUser=await usersAPI.create(createForm); setUsers(u=>[...u,newUser]); setShowCreate(false); setCreateForm({name:"",email:"",password:"",role:"user"}); flash("User created successfully."); }
    catch(ex) { setCreateErr(ex.message); }
    finally { setCreating(false); }
  }

  if (loading) return <div style={{padding:40}}><Spinner G={G} /></div>;

  const sortedUsers = [...users].sort((a,b)=>{
    if (a.active===false&&b.active!==false) return 1;
    if (a.active!==false&&b.active===false) return -1;
    return 0;
  });

  // Available roles in the dropdown — superadmin cannot be assigned via UI
  const assignableRoles = ["user","contractor","contractor_pending","ap_ar","admin"];

  return (
    <div style={{maxWidth:1100,margin:"0 auto",padding:isMobile?"12px":"32px 16px"}} className="page-enter">
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:isMobile?24:34,letterSpacing:".04em",marginBottom:20,color:G.text}}>
        <span style={{color:G.accent}}>//</span> ADMIN PANEL
        {isSuperadmin&&<span style={{fontSize:12,fontWeight:600,color:G.accent,marginLeft:12,letterSpacing:".1em"}}>SUPERADMIN MODE</span>}
        {isSuperadmin&&systemConfig&&!systemConfig.emailsEnabled&&<span style={{fontSize:12,fontWeight:700,color:"#ef4444",marginLeft:12,letterSpacing:".1em",padding:"2px 8px",border:"1px solid #ef4444",borderRadius:4}}>EMAILS PAUSED</span>}
      </div>
      <Alert msg={msg} type={msgType} G={G} />
      {isSuperadmin&&systemConfig&&!systemConfig.emailsEnabled&&(
        <div style={{background:"#ef444411",border:"1px solid #ef4444",borderRadius:G.radius,padding:"12px 16px",marginBottom:16,color:"#ef4444",fontSize:14,display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:18}}>⚠</span>
          <div style={{flex:1}}>
            <div style={{fontWeight:700,letterSpacing:".04em"}}>EMAIL NOTIFICATIONS ARE PAUSED</div>
            <div style={{fontSize:12,color:"#ef4444aa",marginTop:2}}>No outbound emails are being sent. Re-enable from the System tab.</div>
          </div>
          {adminTab!=="system"&&<Btn size="sm" variant="ghost" onClick={()=>setAdminTab("system")} G={G}>Go to System</Btn>}
        </div>
      )}
      <div style={{display:"grid",gridTemplateColumns:isMobile?"repeat(3,1fr)":"repeat(4,1fr)",gap:8,marginBottom:20}}>
        {STATUS_TABS.filter(s=>s!=="Closed/Completed").map(s=>(
          <div key={s} style={{background:G.surface,border:`1px solid ${G.border}`,borderTop:`3px solid ${STATUS_COLORS[s]}`,borderRadius:G.radius,padding:"12px 14px",textAlign:"center"}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:28,color:STATUS_COLORS[s]}}>{stats[s]||0}</div>
            <div style={{fontSize:10,color:G.muted,textTransform:"uppercase",letterSpacing:".06em",marginTop:2}}>{s}</div>
          </div>
        ))}
        <div style={{background:G.surface,border:`1px solid ${G.border}`,borderTop:"3px solid #60a5fa",borderRadius:G.radius,padding:"12px 14px",textAlign:"center"}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:28,color:"#60a5fa"}}>{stats.pendingApplications||0}</div>
          <div style={{fontSize:10,color:G.muted,textTransform:"uppercase",letterSpacing:".06em",marginTop:2}}>Pending Apps</div>
        </div>
      </div>
      <div style={{display:"flex",gap:4,marginBottom:20,borderBottom:`1px solid ${G.border}`}}>
        {[["users","Users"],["applications","Contractor Applications"],...(isSuperadmin?[["import","Import"],["system","System"]]:[])].map(([t,label])=>(
          <button key={t} onClick={()=>setAdminTab(t)} style={{padding:"10px 18px",background:"none",border:"none",borderBottom:`3px solid ${adminTab===t?G.accent:"transparent"}`,color:adminTab===t?G.accent:G.muted,fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,letterSpacing:".06em",cursor:"pointer"}}>
            {label}{t==="applications"&&stats.pendingApplications>0?<span style={{marginLeft:6,background:"#ef444433",color:"#ef4444",borderRadius:10,padding:"1px 7px",fontSize:12}}>{stats.pendingApplications}</span>:null}
          </button>
        ))}
      </div>
      {adminTab==="applications"&&<ApplicationsPanel G={G} />}
      {adminTab==="import"&&isSuperadmin&&<ImportPanel G={G} />}
      {adminTab==="system"&&isSuperadmin&&<SystemPanel G={G} config={systemConfig} onUpdate={setSystemConfig} flash={flash} />}
      {adminTab==="users"&&(
        <div style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,overflow:"hidden"}}>
          <div style={{padding:"16px 20px",borderBottom:`1px solid ${G.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <span style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:20,letterSpacing:".04em",color:G.text}}>USER MANAGEMENT</span>
            {isSuperadmin&&<Btn size="sm" onClick={()=>setShowCreate(true)} G={G}>+ Create User</Btn>}
          </div>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{borderBottom:`1px solid ${G.border}`}}>
                  {["Name","Email","Role","Joined",...(isSuperadmin?["Change Role"]:[]),"Status",...(isSuperadmin?["Delete"]:[])].map(h=>(
                    <th key={h} style={{padding:"8px 10px",textAlign:"left",fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".06em",whiteSpace:"nowrap"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map(u=>(
                  <tr key={u.id} style={{borderBottom:`1px solid ${G.border}22`,opacity:u.active===false?.45:1}}>
                    <td style={{padding:"10px 10px",fontWeight:600,whiteSpace:"nowrap",color:G.text}}>{u.name}{u.id===currentUser.id&&<span style={{color:G.accent,fontSize:11,marginLeft:6}}>(you)</span>}</td>
                    <td style={{padding:"10px 10px",fontSize:13,color:G.muted}}>{u.email||u.username}</td>
                    <td style={{padding:"10px 10px"}}><Badge text={ROLE_LABELS[u.role]||u.role} color={u.role==="superadmin"?"#a855f7":u.role==="admin"?G.accent:u.role==="contractor_pending"?"#f59e0b":"#60a5fa"} /></td>
                    <td style={{padding:"10px 10px",fontSize:13,color:G.muted,whiteSpace:"nowrap"}}>{fmtDate(u.createdAt)}</td>
                    <td style={{padding:"10px 10px"}}>
                      {isSuperadmin&&(u.id!==currentUser.id&&u.role!=="superadmin"?(
                        <select value={u.role} onChange={e=>changeRole(u.id,e.target.value)} style={{width:"auto",padding:"5px 8px",fontSize:12}}>
                          {assignableRoles.map(r=><option key={r} value={r}>{ROLE_LABELS[r]||r}</option>)}
                        </select>
                      ):<span style={{fontSize:12,color:G.muted}}>-</span>)}
                    </td>
                    <td style={{padding:"10px 10px"}}>
                      {u.id!==currentUser.id&&u.role!=="superadmin"&&(isSuperadmin||u.role!=="admin")?(<Btn size="sm" variant={u.active===false?"secondary":"ghost"} onClick={()=>toggleActive(u)} G={G}>{u.active===false?"Activate":"Deactivate"}</Btn>):<span style={{fontSize:12,color:G.muted}}>-</span>}
                    </td>
                    {isSuperadmin&&(
                      <td style={{padding:"10px 10px"}}>
                        {u.active===false&&u.id!==currentUser.id?(
                          <Btn size="sm" variant="danger" onClick={()=>setConfirmDelete(u)} G={G}>Delete</Btn>
                        ):<span style={{fontSize:12,color:G.muted}}>—</span>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {showCreate&&(
        <Modal title="Create New User" onClose={()=>{setShowCreate(false);setCreateErr("");}} G={G}>
          <Alert msg={createErr} G={G} />
          <form onSubmit={handleCreate}>
            <Field label="Full Name" required G={G}><input value={createForm.name} onChange={e=>setCreateForm({...createForm,name:e.target.value})} placeholder="John Smith" /></Field>
            <Field label="Email" required G={G}><input type="email" value={createForm.email} onChange={e=>setCreateForm({...createForm,email:e.target.value})} placeholder="john@alliedcss.org" /></Field>
            <Field label="Password" required G={G}><input type="password" value={createForm.password} onChange={e=>setCreateForm({...createForm,password:e.target.value})} placeholder="Min 6 characters" /></Field>
            <Field label="Role" G={G}><select value={createForm.role} onChange={e=>setCreateForm({...createForm,role:e.target.value})}>{assignableRoles.map(r=><option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</select></Field>
            <div style={{display:"flex",gap:10,marginTop:8}}><Btn loading={creating} G={G}>Create User</Btn><Btn variant="ghost" onClick={()=>{setShowCreate(false);setCreateErr("");}} G={G}>Cancel</Btn></div>
          </form>
        </Modal>
      )}
      {confirmDelete&&(
        <Modal title="Permanently Delete User" onClose={()=>setConfirmDelete(null)} G={G} width={460} noConfirm>
          <p style={{color:G.text,marginBottom:8}}>You are about to <strong>permanently delete</strong> the following user:</p>
          <div style={{background:G.bg,borderRadius:G.radius,padding:"12px 16px",marginBottom:20}}>
            <div style={{fontWeight:700,color:G.text}}>{confirmDelete.name}</div>
            <div style={{fontSize:13,color:G.muted}}>{confirmDelete.email}</div>
            <div style={{fontSize:12,color:G.muted,marginTop:4}}>Role: {ROLE_LABELS[confirmDelete.role]||confirmDelete.role}</div>
          </div>
          <p style={{color:G.danger,fontSize:13,marginBottom:20}}>Their data will be archived to <code style={{background:G.surface,padding:"2px 6px",borderRadius:4}}>deleted_users.json</code> and removed from the app permanently. This cannot be undone.</p>
          <div style={{display:"flex",gap:10}}>
            <Btn variant="danger" onClick={()=>permanentDelete(confirmDelete.id)} G={G}>Yes, Permanently Delete</Btn>
            <Btn variant="ghost" onClick={()=>setConfirmDelete(null)} G={G}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Clients Page ─────────────────────────────────────────────────────────────
function ClientsPage({ user, G }) {
  const isMobile = useIsMobile();
  const isAdmin = ["admin","superadmin","user","ap_ar"].includes(user.role);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [form, setForm] = useState({ firstName:"", lastName:"", notes:"" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(""); const [msg, setMsg] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [expandedData, setExpandedData] = useState({}); // stores { properties, history } per client id
  // Client de-dup state for the create/edit form
  const [dupExact, setDupExact] = useState([]);
  const [dupFuzzy, setDupFuzzy] = useState([]);
  const dupTimer = useRef();
  const submittingRef = useRef(false);

  // Debounced duplicate check while editing the form. excludeId skips the row being edited.
  useEffect(()=>{
    if (!showForm || (!form.firstName.trim() && !form.lastName.trim())) {
      setDupExact([]); setDupFuzzy([]); return;
    }
    clearTimeout(dupTimer.current);
    dupTimer.current=setTimeout(async()=>{
      try {
        const res=await clientsAPI.check({firstName:form.firstName, lastName:form.lastName, excludeId:editClient?.id});
        setDupExact(res.exact||[]);
        setDupFuzzy(res.fuzzy||[]);
      } catch{}
    },500);
  },[showForm, form.firstName, form.lastName, editClient]);

  async function load() {
    setLoading(true);
    try { setClients(await clientsAPI.list()); }
    catch(e) { setErr(e.message); }
    finally { setLoading(false); }
  }
  useEffect(()=>{ load(); },[]);

  async function expandClient(id) {
    if (expandedId===id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!expandedData[id]) {
      try { const c=await clientsAPI.get(id); setExpandedData(p=>({...p,[id]:{properties:c.properties||[],history:c.history||[]}})); }
      catch{}
    }
  }

  function openCreate() { setForm({firstName:"",lastName:"",notes:""}); setEditClient(null); setErr(""); setShowForm(true); }
  function openEdit(c) { setForm({firstName:c.firstName||"",lastName:c.lastName||"",notes:c.notes||""}); setEditClient(c); setErr(""); setShowForm(true); }

  async function handleSave(e) {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setErr(""); setSaving(true);
    try {
      if (editClient) { const upd=await clientsAPI.update(editClient.id,form); setClients(cs=>cs.map(c=>c.id===editClient.id?upd:c)); }
      else { const c=await clientsAPI.create(form); setClients(cs=>[...cs,c]); }
      setShowForm(false); setMsg(editClient?"Client updated.":"Client created."); setTimeout(()=>setMsg(""),3000);
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); submittingRef.current = false; }
  }

  const filtered = search
    ? clients.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    : clients;

  return (
    <div style={{maxWidth:900,margin:"0 auto",padding:isMobile?"12px":"32px 16px"}} className="page-enter">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,gap:12,flexWrap:"wrap"}}>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:isMobile?26:34,letterSpacing:".04em",color:G.text}}><span style={{color:G.accent}}>//</span> CLIENTS</div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search clients..." style={{width:220}} />
          <Btn onClick={openCreate} G={G}>+ New Client</Btn>
        </div>
      </div>
      <Alert msg={msg} type="success" G={G} />
      {loading ? <Spinner G={G} /> : filtered.length===0 ? (
        <div style={{textAlign:"center",padding:"60px 0",color:G.muted}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20}}>{search?"No clients found":"No clients yet"}</div>
          {!search&&<Btn style={{marginTop:16}} onClick={openCreate} G={G}>Add First Client</Btn>}
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {filtered.map(c=>(
            <div key={c.id} style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,overflow:"hidden"}}>
              <div style={{padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,cursor:"pointer"}} onClick={()=>expandClient(c.id)}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                    <span style={{fontWeight:700,fontSize:16,color:G.text}}>{c.name}</span>
                  </div>
                  {c.notes&&<div style={{fontSize:12,color:G.muted,marginTop:4}}>{c.notes}</div>}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <Btn size="sm" variant="ghost" onClick={e=>{e.stopPropagation();openEdit(c);}} G={G}>Edit</Btn>
                  <span style={{color:G.muted,fontSize:18,userSelect:"none"}}>{expandedId===c.id?"▲":"▼"}</span>
                </div>
              </div>
              {expandedId===c.id&&(
                <div style={{borderTop:`1px solid ${G.border}`,padding:"14px 18px",background:G.bg}}>
                  <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.muted,marginBottom:10}}>Linked Properties</div>
                  {!expandedData[c.id] ? <Spinner G={G} /> : expandedData[c.id].properties.length===0 ? (
                    <div style={{fontSize:13,color:G.muted}}>No properties linked to this client.</div>
                  ) : (
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {expandedData[c.id].properties.map(p=>(
                        <div key={p.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:G.surface,borderRadius:G.radius,border:`1px solid ${G.border}`}}>
                          <div>
                            <div style={{fontWeight:600,fontSize:13,color:G.text}}>{[p.street,p.unit].filter(Boolean).join(" ")}</div>
                            <div style={{fontSize:12,color:G.muted}}>{[p.city,p.state,p.zip].filter(Boolean).join(", ")} · {p.propertyType}</div>
                          </div>
                          <Badge text={p.status} color={PROPERTY_STATUS_COLORS[p.status]||G.muted} />
                        </div>
                      ))}
                    </div>
                  )}
                  {expandedData[c.id]?.history?.length>0&&(
                    <div style={{marginTop:16}}>
                      <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.muted,marginBottom:8}}>Change History</div>
                      <div style={{display:"flex",flexDirection:"column",gap:4}}>
                        {[...expandedData[c.id].history].reverse().map((h,i)=>(
                          <div key={i} style={{display:"flex",gap:10,fontSize:12,padding:"6px 10px",background:G.surface,borderRadius:G.radius}}>
                            <span style={{color:G.muted,whiteSpace:"nowrap",flexShrink:0}}>{fmtDate(h.at)}</span>
                            <span style={{color:G.text,flex:1}}>{h.msg}</span>
                            <span style={{color:G.muted,whiteSpace:"nowrap",flexShrink:0}}>{h.by}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {showForm&&(
        <Modal title={editClient?"Edit Client":"New Client"} onClose={()=>setShowForm(false)} G={G} width={480}>
          <Alert msg={err} G={G} />
          <form onSubmit={handleSave}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <Field label="First Name" required G={G}><input value={form.firstName} onChange={e=>setForm({...form,firstName:e.target.value})} placeholder="Jane" autoFocus /></Field>
              <Field label="Last Name" required G={G}><input value={form.lastName} onChange={e=>setForm({...form,lastName:e.target.value})} placeholder="Smith" /></Field>
            </div>
            {/* Exact duplicate — hard block */}
            {dupExact.length>0&&(
              <div style={{padding:"10px 12px",background:"#ef444418",border:`1px solid #ef4444`,borderRadius:G.radius,marginBottom:12}}>
                <div style={{fontSize:12,fontWeight:700,color:"#ef4444",marginBottom:6}}>⚠ {editClient?"Another client with this name already exists":"This client already exists"}</div>
                {dupExact.map(c=>(
                  <div key={c.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 10px",background:G.surface,borderRadius:G.radius,marginBottom:4,gap:10}}>
                    <div style={{fontSize:13,fontWeight:600,color:G.text}}>{c.name}</div>
                    {!editClient&&<Btn size="sm" variant="ghost" type="button" onClick={()=>{ setShowForm(false); setExpandedId(c.id); setDupExact([]); setDupFuzzy([]); }} G={G}>View existing</Btn>}
                  </div>
                ))}
                <div style={{fontSize:11,color:G.muted,marginTop:4}}>Change the name above if this is a different person.</div>
              </div>
            )}
            {/* Fuzzy match — soft warn */}
            {dupExact.length===0&&dupFuzzy.length>0&&(
              <div style={{padding:"10px 12px",background:"#eab30818",border:`1px solid #eab308`,borderRadius:G.radius,marginBottom:12}}>
                <div style={{fontSize:12,fontWeight:700,color:"#eab308",marginBottom:6}}>Did you mean one of these?</div>
                {dupFuzzy.map(c=>(
                  <div key={c.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 10px",background:G.surface,borderRadius:G.radius,marginBottom:4,gap:10}}>
                    <div style={{fontSize:13,fontWeight:600,color:G.text}}>{c.name}</div>
                    {!editClient&&<Btn size="sm" variant="ghost" type="button" onClick={()=>{ setShowForm(false); setExpandedId(c.id); setDupExact([]); setDupFuzzy([]); }} G={G}>View existing</Btn>}
                  </div>
                ))}
                <div style={{fontSize:11,color:G.muted,marginTop:4}}>You can still save if these aren't the same person.</div>
              </div>
            )}
            <Field label="Notes" G={G}><textarea rows={3} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Any relevant notes about this client" style={{resize:"vertical"}} /></Field>
            <div style={{display:"flex",gap:10,marginTop:8}}>
              <Btn loading={saving} disabled={dupExact.length>0} G={G}>{editClient?"Save Changes":"Create Client"}</Btn>
              <Btn variant="ghost" onClick={()=>setShowForm(false)} G={G}>Cancel</Btn>
            </div>          </form>
        </Modal>
      )}
    </div>
  );
}

// ── Contact Editor subcomponent ───────────────────────────────────────────────
function ContactEditor({ contacts, onChange, G }) {
  function addContact() { onChange([...contacts,{id:"new-"+Date.now(),name:"",relationship:"",phones:[{label:"",number:""}],emails:[{label:"",address:""}]}]); }
  function removeContact(i) { onChange(contacts.filter((_,j)=>j!==i)); }
  function updateContact(i,field,val) { const c=[...contacts]; c[i]={...c[i],[field]:val}; onChange(c); }
  function addPhone(i) { const c=[...contacts]; c[i]={...c[i],phones:[...c[i].phones,{label:"",number:""}]}; onChange(c); }
  function removePhone(i,j) { const c=[...contacts]; c[i]={...c[i],phones:c[i].phones.filter((_,k)=>k!==j)}; onChange(c); }
  function updatePhone(i,j,field,val) { const c=[...contacts]; c[i]={...c[i],phones:c[i].phones.map((p,k)=>k===j?{...p,[field]:val}:p)}; onChange(c); }
  function addEmail(i) { const c=[...contacts]; c[i]={...c[i],emails:[...c[i].emails,{label:"",address:""}]}; onChange(c); }
  function removeEmail(i,j) { const c=[...contacts]; c[i]={...c[i],emails:c[i].emails.filter((_,k)=>k!==j)}; onChange(c); }
  function updateEmail(i,j,field,val) { const c=[...contacts]; c[i]={...c[i],emails:c[i].emails.map((e,k)=>k===j?{...e,[field]:val}:e)}; onChange(c); }
  return (
    <div>
      {contacts.map((ct,i)=>(
        <div key={ct.id||i} style={{background:"rgba(0,0,0,.15)",borderRadius:6,padding:14,marginBottom:12,border:`1px solid ${G.border}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <span style={{fontSize:12,fontWeight:700,color:G.accent,textTransform:"uppercase",letterSpacing:".06em"}}>Contact {i+1}</span>
            <button type="button" onClick={()=>removeContact(i)} style={{background:"none",border:"none",color:G.danger,fontSize:16,cursor:"pointer"}}>✕</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <Field label="Name" G={G}><input value={ct.name} onChange={e=>updateContact(i,"name",e.target.value)} placeholder="Contact name" /></Field>
            <Field label="Relationship" G={G}><input value={ct.relationship} onChange={e=>updateContact(i,"relationship",e.target.value)} placeholder="e.g. Property Manager" /></Field>
          </div>
          <div style={{marginBottom:8}}>
            <div style={{fontSize:11,color:G.muted,fontWeight:700,letterSpacing:".06em",textTransform:"uppercase",marginBottom:6}}>Phone Numbers</div>
            {(ct.phones||[]).map((ph,j)=>(
              <div key={j} style={{marginBottom:8}}>
                <div style={{display:"flex",gap:8,marginBottom:ph.label==="Work"?4:0,alignItems:"center"}}>
                  <select value={ph.label} onChange={e=>updatePhone(i,j,"label",e.target.value)} style={{width:90,flex:"0 0 90px"}}>
                    {["","Cell","Home","Work","Direct","Fax"].map(l=><option key={l} value={l}>{l||"Label"}</option>)}
                  </select>
                  <input value={ph.number} onChange={e=>updatePhone(i,j,"number",e.target.value)} onBlur={e=>updatePhone(i,j,"number",formatPhone(e.target.value))} placeholder="(555) 555-5555" style={{flex:1}} />
                  <button type="button" onClick={()=>removePhone(i,j)} style={{background:"none",border:"none",color:G.danger,cursor:"pointer",fontSize:16,flexShrink:0}}>✕</button>
                </div>
                {ph.label==="Work"&&<input value={ph.ext||""} onChange={e=>updatePhone(i,j,"ext",e.target.value)} placeholder="Ext. (optional)" style={{width:140,marginLeft:98}} />}
              </div>
            ))}
            <button type="button" onClick={()=>addPhone(i)} style={{background:"none",border:`1px dashed ${G.border}`,color:G.muted,borderRadius:G.radius,padding:"4px 12px",fontSize:12,cursor:"pointer",marginTop:2}}>+ Add Phone</button>
          </div>
          <div>
            <div style={{fontSize:11,color:G.muted,fontWeight:700,letterSpacing:".06em",textTransform:"uppercase",marginBottom:6}}>Email Addresses</div>
            {(ct.emails||[]).map((em,j)=>(
              <div key={j} style={{display:"flex",gap:8,marginBottom:6,alignItems:"center"}}>
                <select value={em.label} onChange={e=>updateEmail(i,j,"label",e.target.value)} style={{width:90,flex:"0 0 90px"}}>
                  {["","Work","Personal","Other"].map(l=><option key={l} value={l}>{l||"Label"}</option>)}
                </select>
                <input value={em.address} onChange={e=>updateEmail(i,j,"address",e.target.value)} placeholder="Email address" style={{flex:1}} />
                <button type="button" onClick={()=>removeEmail(i,j)} style={{background:"none",border:"none",color:G.danger,cursor:"pointer",fontSize:16,flexShrink:0}}>✕</button>
              </div>
            ))}
            <button type="button" onClick={()=>addEmail(i)} style={{background:"none",border:`1px dashed ${G.border}`,color:G.muted,borderRadius:G.radius,padding:"4px 12px",fontSize:12,cursor:"pointer",marginTop:2}}>+ Add Email</button>
          </div>
        </div>
      ))}
      <button type="button" onClick={addContact} style={{width:"100%",background:"none",border:`1px dashed ${G.border}`,color:G.accent,borderRadius:G.radius,padding:"10px",fontSize:13,cursor:"pointer",fontWeight:600}}>+ Add Contact</button>
    </div>
  );
}

// ── Properties Page ───────────────────────────────────────────────────────────
function PropertiesPage({ user, G, defaultView="list", onTicketClick }) {
  const isMobile = useIsMobile();
  const isAdmin = ["admin","superadmin"].includes(user.role);
  const [properties, setProperties] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(defaultView); // "list" | "map"
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editProp, setEditProp] = useState(null);
  const [viewProp, setViewProp] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(""); const [msg, setMsg] = useState("");
  const [mapsKey, setMapsKey] = useState(null);
  const [form, setForm] = useState({ street:"",unit:"",city:"New York",state:"NY",zip:"",propertyType:"Single-Family",status:"Needs 1st Visit",keyStatus:"Keys Available",clientIds:[],contacts:[],notes:"" });
  const submittingRef = useRef(false);

  async function load() {
    setLoading(true);
    try {
      const [props, cls] = await Promise.all([
        propertiesAPI.list({search,status:statusFilter,includeInactive:includeInactive?"true":""}),
        clientsAPI.list(),
      ]);
      setProperties(props); setClients(cls);
    } catch(e) { setErr(e.message); }
    finally { setLoading(false); }
  }
  useEffect(()=>{ load(); },[search,statusFilter,includeInactive]);

  // Load Maps key when map view is first requested
  useEffect(()=>{
    if (viewMode==="map"&&!mapsKey) {
      mapsAPI.getKey().then(d=>setMapsKey(d.key)).catch(()=>{});
    }
  },[viewMode]);

  function openCreate() { setForm({street:"",unit:"",city:"New York",state:"NY",zip:"",propertyType:"Single-Family",status:"Needs 1st Visit",keyStatus:"Keys Available",clientIds:[],contacts:[],notes:""}); setEditProp(null); setErr(""); setNewClientForm({firstName:"",lastName:"",notes:""}); setAddNewClient(false); setShowForm(true); }
  function openEdit(p) { setForm({street:p.street,unit:p.unit||"",city:p.city,state:p.state,zip:p.zip||"",propertyType:p.propertyType||"Single-Family",status:p.status,keyStatus:p.keyStatus||"Keys Available",clientIds:p.clientIds||[],contacts:p.contacts||[],notes:p.notes||""}); setEditProp(p); setErr(""); setAddNewClient(false); setShowForm(true); }

  const [addNewClient, setAddNewClient] = useState(false);
  const [newClientForm, setNewClientForm] = useState({firstName:"",lastName:"",notes:""});

  async function handleSave(e) {
    e.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setErr(""); setSaving(true);
    try {
      let finalClientIds = [...form.clientIds];
      // If "New Client" is checked, create the client first
      if (!editProp && addNewClient && newClientForm.firstName.trim() && newClientForm.lastName.trim()) {
        const newClient = await clientsAPI.create(newClientForm);
        setClients(cs=>[...cs, newClient]);
        finalClientIds = [...finalClientIds, newClient.id];
      }
      const saveForm = { ...form, clientIds: finalClientIds };
      if (editProp) { const upd=await propertiesAPI.update(editProp.id,saveForm); setProperties(ps=>ps.map(p=>p.id===editProp.id?{...upd,clientNames:clients.filter(c=>saveForm.clientIds.includes(c.id)).map(c=>c.name)}:p)); }
      else { const p=await propertiesAPI.create(saveForm); setProperties(ps=>[{...p,clientNames:clients.filter(c=>saveForm.clientIds.includes(c.id)).map(c=>c.name)},...ps]); }
      setShowForm(false); setMsg(editProp?"Property updated.":"Property added."); setTimeout(()=>setMsg(""),3000);
    } catch(ex) { setErr(ex.message); }
    finally { setSaving(false); submittingRef.current = false; }
  }

  async function handleArchive(id) {
    try { await propertiesAPI.archive(id); setProperties(ps=>ps.filter(p=>p.id!==id)); setViewProp(null); setMsg("Property archived."); setTimeout(()=>setMsg(""),3000); }
    catch(ex) { setErr(ex.message); }
  }

  function toggleClientId(id) { setForm(f=>({...f,clientIds:f.clientIds.includes(id)?f.clientIds.filter(x=>x!==id):[...f.clientIds,id]})); }

  // Pin color is now fully computed by the backend — just use priorityColor
  function pinColor(p) { return p.priorityColor || "#22c55e"; }

  return (
    <div style={{maxWidth:1100,margin:"0 auto",padding:isMobile?"12px":"32px 16px"}} className="page-enter">
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,gap:12,flexWrap:"wrap"}}>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:isMobile?26:34,letterSpacing:".04em",color:G.text}}><span style={{color:G.accent}}>//</span> {defaultView==="map"?"DASHBOARD":"PROPERTIES"}</div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
          <div style={{display:"flex",background:G.surface,border:`1px solid ${G.border}`,borderRadius:G.radius,overflow:"hidden"}}>
            {[["list","☰ List"],["map","◎ Map"]].map(([v,label])=>(
              <button key={v} onClick={()=>setViewMode(v)} style={{padding:"7px 16px",background:viewMode===v?G.accent+"22":"none",border:"none",color:viewMode===v?G.accent:G.muted,fontWeight:700,fontSize:13,cursor:"pointer"}}>{label}</button>
            ))}
          </div>
          <Btn variant="ghost" onClick={load} G={G} title="Refresh">↻</Btn>
          <Btn onClick={openCreate} G={G}>+ Add Property</Btn>
        </div>
      </div>
      {/* Filters */}
      <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search address or client..." style={{width:220}} />
        <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={{width:180}}>
          <option value="">All Statuses</option>
          {PROPERTY_STATUSES.map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        <label style={{display:"flex",alignItems:"center",gap:6,margin:0,textTransform:"none",fontSize:13,color:G.muted,cursor:"pointer",fontWeight:400,letterSpacing:"normal"}}>
          <input type="checkbox" checked={includeInactive} onChange={e=>setIncludeInactive(e.target.checked)} />
          Show archived
        </label>
      </div>
      <Alert msg={msg} type="success" G={G} />
      <Alert msg={err} G={G} />
      {loading ? <Spinner G={G} /> : viewMode==="list" ? (
        <PropertyListView properties={properties} onView={setViewProp} onEdit={openEdit} pinColor={pinColor} G={G} isMobile={isMobile} />
      ) : (
        <PropertyMapView properties={properties} mapsKey={mapsKey} pinColor={pinColor} onSelect={setViewProp} G={G} />
      )}
      {/* Property form modal */}
      {showForm&&(
        <Modal title={editProp?"Edit Property":"Add Property"} onClose={()=>setShowForm(false)} G={G} width={640}>
          <Alert msg={err} G={G} />
          <form onSubmit={handleSave}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,color:G.accent,marginBottom:12,letterSpacing:".04em"}}>ADDRESS</div>
            <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:12}}>
              <Field label="Street Address" required G={G}><input value={form.street} onChange={e=>setForm({...form,street:e.target.value})} placeholder="123 Main Street" /></Field>
              <Field label="Unit" G={G}><input value={form.unit} onChange={e=>setForm({...form,unit:e.target.value})} placeholder="Apt 2B" /></Field>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:12}}>
              <Field label="City" required G={G}><input value={form.city} onChange={e=>setForm({...form,city:e.target.value})} placeholder="New York" /></Field>
              <Field label="State" required G={G}><input value={form.state} onChange={e=>setForm({...form,state:e.target.value})} placeholder="NY" /></Field>
              <Field label="Zip" G={G}><input value={form.zip} onChange={e=>setForm({...form,zip:e.target.value})} placeholder="10001" /></Field>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <Field label="Property Type" G={G}><select value={form.propertyType} onChange={e=>setForm({...form,propertyType:e.target.value})}>{PROPERTY_TYPES.map(t=><option key={t}>{t}</option>)}</select></Field>
              <Field label="Status" G={G}><select value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>{PROPERTY_STATUSES.map(s=><option key={s}>{s}</option>)}</select></Field>
            </div>
            <Field label="Key Status" required hint="Drives whether the auto-generated first-visit ticket needs a locksmith." G={G}>
              <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                {KEY_STATUSES.map(k=>(
                  <label key={k} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",background:form.keyStatus===k?G.accent+"22":G.bg,border:`1px solid ${form.keyStatus===k?G.accent:G.border}`,borderRadius:G.radius,cursor:"pointer",textTransform:"none",fontSize:13,color:G.text,fontWeight:form.keyStatus===k?600:400,letterSpacing:"normal",margin:0,flex:"1 1 200px"}}>
                    <input type="radio" name="prop-keyStatus" value={k} checked={form.keyStatus===k} onChange={()=>setForm(f=>({...f,keyStatus:k}))} />
                    {k}
                  </label>
                ))}
              </div>
            </Field>
            <div style={{marginBottom:16}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,color:G.accent,marginBottom:10,letterSpacing:".04em"}}>LINKED CLIENTS</div>
              {clients.length===0&&!addNewClient?<div style={{fontSize:13,color:G.muted,marginBottom:8}}>No clients yet.</div>:(
                <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:10}}>
                  {clients.map(c=>(
                    <label key={c.id} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",background:form.clientIds.includes(c.id)?G.accent+"22":G.bg,border:`1px solid ${form.clientIds.includes(c.id)?G.accent:G.border}`,borderRadius:G.radius,cursor:"pointer",textTransform:"none",fontSize:13,color:G.text,fontWeight:form.clientIds.includes(c.id)?600:400,margin:0,letterSpacing:"normal"}}>
                      <input type="checkbox" checked={form.clientIds.includes(c.id)} onChange={()=>toggleClientId(c.id)} style={{width:14}} />
                      {c.name}
                    </label>
                  ))}
                </div>
              )}
              {/* New Client inline toggle — only on create */}
              {!editProp&&(
                <div>
                  <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",textTransform:"none",fontSize:13,color:addNewClient?G.accent:G.muted,fontWeight:addNewClient?700:400,letterSpacing:"normal",margin:0,padding:"8px 12px",background:addNewClient?G.accent+"12":"transparent",border:`1px solid ${addNewClient?G.accent:G.border}`,borderRadius:G.radius,userSelect:"none"}}>
                    <input type="checkbox" checked={addNewClient} onChange={e=>setAddNewClient(e.target.checked)} style={{width:15,height:15,accentColor:G.accent}} />
                    ✚ New Client
                  </label>
                  {addNewClient&&(
                    <div style={{marginTop:10,padding:"14px 16px",background:G.bg,borderRadius:G.radius,border:`1px solid ${G.accent}44`}}>
                      <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.accent,marginBottom:10}}>New Client Details</div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                        <Field label="First Name" required G={G}><input value={newClientForm.firstName} onChange={e=>setNewClientForm({...newClientForm,firstName:e.target.value})} placeholder="Jane" /></Field>
                        <Field label="Last Name" required G={G}><input value={newClientForm.lastName} onChange={e=>setNewClientForm({...newClientForm,lastName:e.target.value})} placeholder="Smith" /></Field>
                      </div>
                      <Field label="Notes" G={G}><textarea rows={2} value={newClientForm.notes} onChange={e=>setNewClientForm({...newClientForm,notes:e.target.value})} placeholder="Any relevant notes" style={{resize:"vertical"}} /></Field>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={{marginBottom:16}}>
              <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:15,color:G.accent,marginBottom:10,letterSpacing:".04em"}}>CONTACTS</div>
              <ContactEditor contacts={form.contacts} onChange={v=>setForm({...form,contacts:v})} G={G} />
            </div>
            <Field label="Notes" G={G}><textarea rows={3} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Any relevant notes about this property" style={{resize:"vertical"}} /></Field>
            <div style={{display:"flex",gap:10,marginTop:8}}>
              <Btn loading={saving} G={G}>{editProp?"Save Changes":"Add Property"}</Btn>
              <Btn variant="ghost" onClick={()=>setShowForm(false)} G={G}>Cancel</Btn>
            </div>          </form>
        </Modal>
      )}
      {/* Property detail panel */}
      {viewProp&&<PropertyDetailPanel prop={viewProp} onEdit={()=>{setViewProp(null);openEdit(viewProp);}} onArchive={isAdmin?()=>handleArchive(viewProp.id):null} onClose={()=>setViewProp(null)} onTicketClick={onTicketClick} G={G} isMobile={isMobile} />}
    </div>
  );
}

function PropertyListView({ properties, onView, onEdit, pinColor, G, isMobile }) {
  if (properties.length===0) return (
    <div style={{textAlign:"center",padding:"60px 0",color:G.muted}}>
      <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontSize:20}}>No properties found</div>
    </div>
  );
  return (
    <div style={{display:"flex",flexDirection:"column",gap:8}}>
      {properties.map(p=>{
        const color=pinColor(p);
        const isInactive=!!p.archivedAt;
        return (
          <div key={p.id} onClick={()=>onView(p)} className="card-hover" style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,padding:"14px 18px",cursor:"pointer",borderLeft:`4px solid ${color}`,opacity:isInactive?.6:1}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:4}}>
                  <span style={{fontWeight:700,fontSize:15,color:G.text}}>{[p.street,p.unit].filter(Boolean).join(" ")}</span>
                  {p.priorityColor==="#ef4444"&&<Badge text="⚡ EMERGENCY" color="#ef4444" />}
                  {p.priorityColor==="#f97316"&&<Badge text="⚠ URGENT" color="#f97316" />}
                  {isInactive&&<Badge text="Archived" color={G.muted} />}
                </div>
                <div style={{fontSize:13,color:G.muted}}>{[p.city,p.state,p.zip].filter(Boolean).join(", ")} · {p.propertyType}</div>
                {p.clientNames?.length>0&&<div style={{fontSize:12,color:G.accent,marginTop:3}}>Client: {p.clientNames.join(", ")}</div>}
              </div>
              <div style={{flexShrink:0}}>
                <Badge text={p.status} color={p.priorityColor||G.muted} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PropertyMapView({ properties, mapsKey, pinColor, onSelect, G }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [mapError, setMapError] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  // Load Google Maps script
  useEffect(()=>{
    if (!mapsKey) return;
    if (window.google?.maps) { setMapReady(true); return; }
    const existing = document.getElementById("gmap-script");
    if (existing) { existing.onload=()=>setMapReady(true); return; }
    const script = document.createElement("script");
    script.id = "gmap-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${mapsKey}`;
    script.async = true; script.defer = true;
    script.onload = ()=>setMapReady(true);
    script.onerror = ()=>setMapError("Failed to load Google Maps.");
    document.head.appendChild(script);
  },[mapsKey]);

  // Init map
  useEffect(()=>{
    if (!mapReady||!mapRef.current) return;
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center:{lat:40.8448,lng:-73.6601}, zoom:9,
      styles:[
        {elementType:"geometry",stylers:[{color:"#1d2030"}]},
        {elementType:"labels",stylers:[{visibility:"off"}]},
        {featureType:"administrative.locality",elementType:"labels.text.fill",stylers:[{color:"#4a5068"},{visibility:"on"}]},
        {featureType:"administrative.locality",elementType:"labels.text.stroke",stylers:[{color:"#1d2030"},{weight:2},{visibility:"on"}]},
        {featureType:"administrative.neighborhood",elementType:"labels",stylers:[{visibility:"off"}]},
        {featureType:"road",elementType:"geometry",stylers:[{color:"#2d3346"}]},
        {featureType:"road.highway",elementType:"geometry",stylers:[{color:"#3a4060"}]},
        {featureType:"road",elementType:"labels",stylers:[{visibility:"off"}]},
        {featureType:"water",elementType:"geometry",stylers:[{color:"#0f1117"}]},
        {featureType:"water",elementType:"labels",stylers:[{visibility:"off"}]},
        {featureType:"poi",stylers:[{visibility:"off"}]},
        {featureType:"transit",stylers:[{visibility:"off"}]},
      ],
      mapTypeControl:false, streetViewControl:false, fullscreenControl:true,
    });
  },[mapReady]);

  // Place pins
  useEffect(()=>{
    if (!mapInstanceRef.current) return;
    markersRef.current.forEach(m=>m.setMap(null)); markersRef.current=[];
    const bounds = new window.google.maps.LatLngBounds();
    let hasPin=false;
    properties.filter(p=>p.lat&&p.lng).forEach(p=>{
      const color = pinColor(p);
      const marker = new window.google.maps.Marker({
        position:{lat:p.lat,lng:p.lng},
        map:mapInstanceRef.current,
        title:[p.street,p.city].filter(Boolean).join(", "),
        icon:{path:window.google.maps.SymbolPath.CIRCLE,fillColor:color,fillOpacity:1,strokeColor:"#fff",strokeWeight:2,scale:10},
      });
      marker.addListener("click",()=>onSelect(p));
      markersRef.current.push(marker);
      bounds.extend({lat:p.lat,lng:p.lng}); hasPin=true;
    });
    if (hasPin) mapInstanceRef.current.fitBounds(bounds);
  },[properties,mapReady]);

  const geocodedCount = properties.filter(p=>p.lat&&p.lng).length;

  if (!mapsKey) return <div style={{textAlign:"center",padding:60,color:G.muted}}><Spinner G={G} /></div>;
  if (mapError) return <div style={{textAlign:"center",padding:60,color:G.danger}}>{mapError}</div>;

  return (
    <div>
      <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap",alignItems:"center"}}>
        <span style={{fontSize:12,color:G.muted}}>{geocodedCount} of {properties.length} properties have map coordinates</span>
        {geocodedCount<properties.length&&<span style={{fontSize:12,color:G.accent}}>— Properties without coordinates won't appear as pins</span>}
      </div>
      {/* Legend */}
      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:12}}>
        {[["Emergency","#ef4444"],["Urgent","#f97316"],["To-Do","#eab308"],["Status Quo","#22c55e"],["On Market","#a855f7"],["No Longer Ours","#9ca3af80"]].map(([label,color])=>(
          <div key={label} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:G.muted}}>
            <div style={{width:12,height:12,borderRadius:"50%",background:color,border:"2px solid #fff",flexShrink:0}} />{label}
          </div>
        ))}
      </div>
      <div ref={mapRef} style={{width:"100%",height:560,borderRadius:8,border:`1px solid ${G.border}`,background:G.surface}} />
    </div>
  );
}

function PropertyDetailPanel({ prop, onEdit, onArchive, onClose, onTicketClick, G, isMobile }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState("");
  useEffect(()=>{
    propertiesAPI.get(prop.id).then(d=>setDetail(d)).finally(()=>setLoading(false));
  },[prop.id]);

  const linkedClients = detail?.clients||[];
  const tickets = detail?.tickets||[];
  const contacts = detail?.contacts||[];

  return (
    <>
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",zIndex:100,display:"flex",alignItems:"flex-start",justifyContent:"flex-end"}} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:G.surface,borderLeft:`1px solid ${G.border}`,width:isMobile?"100%":480,height:"100%",overflowY:"auto",display:"flex",flexDirection:"column"}} className="page-enter">
        <div style={{padding:"18px 20px",borderBottom:`1px solid ${G.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",background:G.bg,position:"sticky",top:0,zIndex:2}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:18,color:G.text,letterSpacing:".04em"}}>PROPERTY DETAIL</div>
          <button onClick={onClose} style={{background:"none",border:"none",color:G.muted,fontSize:22,cursor:"pointer",lineHeight:1}}>×</button>
        </div>
        {loading?<div style={{padding:40}}><Spinner G={G} /></div>:(
          <div style={{padding:20,display:"flex",flexDirection:"column",gap:20}}>
            {/* Address + status */}
            <div style={{borderLeft:`4px solid ${PROPERTY_STATUS_COLORS[prop.status]||G.border}`,paddingLeft:14}}>
              <div style={{fontWeight:700,fontSize:18,color:G.text}}>{[prop.street,prop.unit].filter(Boolean).join(" ")}</div>
              <div style={{color:G.muted,fontSize:14}}>{[prop.city,prop.state,prop.zip].filter(Boolean).join(", ")}</div>
              <div style={{display:"flex",gap:8,marginTop:8,flexWrap:"wrap"}}>
                <Badge text={prop.status} color={PROPERTY_STATUS_COLORS[prop.status]||G.muted} />
                {prop.priorityColor==="#ef4444"&&<Badge text="⚡ EMERGENCY" color="#ef4444" />}
                {prop.priorityColor==="#f97316"&&<Badge text="⚠ URGENT" color="#f97316" />}
                {prop.propertyType&&<Badge text={prop.propertyType} color={G.muted} />}
                {prop.keyStatus&&<Badge text={prop.keyStatus==="Locksmith Required"?"🔒 Locksmith Required":"🔑 Keys Available"} color={prop.keyStatus==="Locksmith Required"?"#f59e0b":"#22c55e"} />}
              </div>
            </div>
            <div style={{display:"flex",gap:8}}>
              <Btn size="sm" onClick={onEdit} G={G}>Edit Property</Btn>
              {onArchive&&!prop.archivedAt&&<Btn size="sm" variant="danger" onClick={onArchive} G={G}>Archive</Btn>}
            </div>
            {/* Clients */}
            <div>
              <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.muted,marginBottom:8}}>Clients</div>
              {linkedClients.length===0?<div style={{fontSize:13,color:G.muted}}>No clients linked.</div>:(
                linkedClients.map(c=>(
                  <div key={c.id} style={{padding:"8px 12px",background:G.bg,borderRadius:G.radius,marginBottom:6}}>
                    <span style={{fontWeight:600,fontSize:14,color:G.text}}>{c.name}</span>
                  </div>
                ))
              )}
            </div>
            {/* Contacts */}
            <div>
              <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.muted,marginBottom:8}}>Contacts</div>
              {contacts.length===0?<div style={{fontSize:13,color:G.muted}}>No contacts listed.</div>:(
                contacts.map((ct,i)=>(
                  <div key={ct.id||i} style={{padding:"12px 14px",background:G.bg,borderRadius:G.radius,marginBottom:8}}>
                    <div style={{fontWeight:700,fontSize:14,color:G.text}}>{ct.name||"—"}</div>
                    {ct.relationship&&<div style={{fontSize:12,color:G.accent,marginBottom:6}}>{ct.relationship}</div>}
                    {(ct.phones||[]).filter(p=>p.number).map((ph,j)=>(
                      <div key={j} style={{fontSize:13,color:G.muted,marginBottom:2}}>📞 {ph.label?`${ph.label}: `:""}<a href={`tel:${ph.number}`} style={{color:G.text}}>{ph.number}</a></div>
                    ))}
                    {(ct.emails||[]).filter(e=>e.address).map((em,j)=>(
                      <div key={j} style={{fontSize:13,color:G.muted,marginBottom:2}}>✉ {em.label?`${em.label}: `:""}<a href={`mailto:${em.address}`} style={{color:G.text}}>{em.address}</a></div>
                    ))}
                  </div>
                ))
              )}
            </div>
            {/* Notes */}
            <div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.muted}}>Notes</div>
                <button onClick={()=>{setNoteDraft(detail?.notes??prop.notes??"");setNoteError("");setNoteModalOpen(true);}} style={{background:"none",border:`1px solid ${G.border}`,color:G.accent,fontSize:11,fontWeight:600,letterSpacing:".04em",padding:"3px 10px",borderRadius:G.radius,cursor:"pointer",textTransform:"uppercase"}}>Edit</button>
              </div>
              {(detail?.notes||prop.notes)
                ?<div style={{fontSize:13,color:G.text,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{detail?.notes||prop.notes}</div>
                :<div style={{fontSize:13,color:G.muted,fontStyle:"italic"}}>No notes yet.</div>}
            </div>
            {/* Ticket history */}
            <div>
              <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.muted,marginBottom:8}}>Ticket History ({tickets.length})</div>
              {tickets.length===0?<div style={{fontSize:13,color:G.muted}}>No tickets for this property.</div>:(
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {[...tickets].sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt)).map(t=>(
                    <div key={t.id} onClick={()=>onTicketClick&&onTicketClick(t)} style={{padding:"10px 12px",background:G.bg,borderRadius:G.radius,borderLeft:`3px solid ${STATUS_COLORS[t.status]||G.border}`,cursor:onTicketClick?"pointer":"default"}} onMouseEnter={e=>{if(onTicketClick)e.currentTarget.style.borderColor=G.accent;}} onMouseLeave={e=>e.currentTarget.style.borderColor=STATUS_COLORS[t.status]||G.border}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <span className="mono" style={{fontSize:11,color:G.muted}}>{t.id}</span>
                        <Badge text={t.status} color={STATUS_COLORS[t.status]||G.muted} />
                      </div>
                      <div style={{fontSize:13,fontWeight:600,color:G.text,marginTop:4}}>{t.workType}</div>
                      <div style={{fontSize:12,color:G.muted,marginTop:2}}>{fmtDate(t.dateReported)} · {t.urgency}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Change history */}
            {detail?.history?.length>0&&(
              <div>
                <div style={{fontSize:11,fontWeight:700,letterSpacing:".08em",textTransform:"uppercase",color:G.muted,marginBottom:8}}>Change History</div>
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  {[...(detail.history||[])].reverse().map((h,i)=>(
                    <div key={i} style={{display:"flex",gap:10,fontSize:12,padding:"6px 10px",background:G.bg,borderRadius:G.radius}}>
                      <span style={{color:G.muted,whiteSpace:"nowrap",flexShrink:0}}>{fmtDate(h.at)}</span>
                      <span style={{color:G.text,flex:1}}>{h.msg}</span>
                      <span style={{color:G.muted,whiteSpace:"nowrap",flexShrink:0}}>{h.by}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    {noteModalOpen&&(
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.88)",zIndex:101,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
        <div style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,width:"100%",maxWidth:720,maxHeight:"88vh",display:"flex",flexDirection:"column",boxShadow:"0 20px 60px rgba(0,0,0,.5)"}} className="page-enter">
          <div style={{padding:"18px 24px",borderBottom:`1px solid ${G.border}`,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:18,color:G.text,letterSpacing:".04em"}}>EDIT NOTES</div>
            <button onClick={()=>{if(!noteSaving)setNoteModalOpen(false);}} disabled={noteSaving} style={{background:"none",border:"none",color:G.muted,fontSize:22,cursor:noteSaving?"not-allowed":"pointer",lineHeight:1,opacity:noteSaving?.5:1}}>×</button>
          </div>
          <div style={{padding:24,flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:12}}>
            <div style={{fontSize:13,color:G.muted}}>{[prop.street,prop.unit].filter(Boolean).join(" ")}</div>
            <Alert msg={noteError} G={G} />
            <textarea
              value={noteDraft}
              onChange={e=>setNoteDraft(e.target.value)}
              disabled={noteSaving}
              autoFocus
              placeholder="Notes about this property…"
              style={{width:"100%",minHeight:360,flex:1,padding:14,background:G.bg,color:G.text,border:`1px solid ${G.border}`,borderRadius:G.radius,fontSize:14,lineHeight:1.6,fontFamily:"inherit",resize:"vertical",boxSizing:"border-box"}}
            />
          </div>
          <div style={{padding:"16px 24px",borderTop:`1px solid ${G.border}`,display:"flex",gap:10,justifyContent:"flex-end"}}>
            <Btn size="sm" variant="ghost" onClick={()=>{if(!noteSaving)setNoteModalOpen(false);}} disabled={noteSaving} G={G}>Cancel</Btn>
            <Btn size="sm" loading={noteSaving} onClick={async()=>{
              setNoteSaving(true); setNoteError("");
              try {
                await propertiesAPI.update(prop.id,{notes:noteDraft});
                setDetail(d=>d?{...d,notes:noteDraft}:d);
                setNoteModalOpen(false);
              } catch(ex) { setNoteError(ex.message||"Save failed"); }
              finally { setNoteSaving(false); }
            }} G={G}>Save Notes</Btn>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// ── Import Panel ──────────────────────────────────────────────────────────────
function ImportPanel({ G }) {
  const isMobile = useIsMobile();
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [err, setErr] = useState("");
  const fileRef = useRef();

  async function handleImport(e) {
    e.preventDefault();
    if (!csvFile) return setErr("Please select a CSV file.");
    setErr(""); setUploading(true); setResults(null);
    try {
      const res = await importAPI.properties(csvFile);
      setResults(res);
      setCsvFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch(ex) { setErr(ex.message); }
    finally { setUploading(false); }
  }

  const statusColor = { created:G.success, skipped:G.muted, error:G.danger };
  const statusIcon  = { created:"✓", skipped:"–", error:"✕" };

  return (
    <div style={{maxWidth:760}}>
      {/* Download template */}
      <div style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,padding:20,marginBottom:16}}>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:16,color:G.accent,marginBottom:8,letterSpacing:".04em"}}>STEP 1 — DOWNLOAD TEMPLATE</div>
        <p style={{color:G.muted,fontSize:13,marginBottom:14,lineHeight:1.6}}>
          Fill out the Excel template with your property and client data. Use the dropdown menus in the Property Type and Status columns. Delete the 3 example rows. Export as <strong style={{color:G.text}}>CSV UTF-8</strong> (File → Save As → CSV UTF-8) before uploading.
        </p>
        <a href={`${process.env.REACT_APP_API_URL||"/api"}/import/template?token=${localStorage.getItem("proptrack_token")}`}
          style={{display:"inline-block",background:G.accent,color:"#000",padding:"9px 20px",borderRadius:G.radius,fontWeight:700,fontSize:14,textDecoration:"none"}}>
          ⬇ Download Template (.xlsx)
        </a>
      </div>
      {/* Upload CSV */}
      <div style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,padding:20,marginBottom:16}}>
        <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:16,color:G.accent,marginBottom:12,letterSpacing:".04em"}}>STEP 2 — UPLOAD CSV</div>
        <Alert msg={err} G={G} />
        <form onSubmit={handleImport}>
          <Field label="Select CSV File" G={G}>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={e=>{ setCsvFile(e.target.files[0]||null); setResults(null); }} style={{padding:"7px 12px"}} />
          </Field>
          {csvFile&&<div style={{fontSize:12,color:G.muted,marginBottom:12}}>Selected: <span style={{color:G.text}}>{csvFile.name}</span> ({(csvFile.size/1024).toFixed(1)} KB)</div>}
          <Btn loading={uploading} disabled={!csvFile} G={G}>Upload &amp; Import</Btn>
        </form>
      </div>
      {/* Results */}
      {results&&(
        <div style={{background:G.surface,border:`1px solid ${G.border}`,borderRadius:8,padding:20}}>
          <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:700,fontSize:16,color:G.accent,marginBottom:14,letterSpacing:".04em"}}>IMPORT RESULTS</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
            {[["Created",results.created,G.success],["Skipped",results.skipped,G.muted],["Errors",results.errors?.length||0,G.danger]].map(([label,count,color])=>(
              <div key={label} style={{background:G.bg,borderRadius:G.radius,padding:"14px 16px",textAlign:"center",border:`1px solid ${color}44`}}>
                <div style={{fontFamily:"'Barlow Condensed',sans-serif",fontWeight:800,fontSize:32,color}}>{count}</div>
                <div style={{fontSize:11,color:G.muted,textTransform:"uppercase",letterSpacing:".06em"}}>{label}</div>
              </div>
            ))}
          </div>
          {results.rows?.length>0&&(
            <div style={{maxHeight:360,overflowY:"auto",border:`1px solid ${G.border}`,borderRadius:G.radius}}>
              {results.rows.map((r,i)=>(
                <div key={i} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"8px 12px",borderBottom:`1px solid ${G.border}`,background:i%2===0?G.bg:"transparent"}}>
                  <span style={{color:statusColor[r.status]||G.muted,fontWeight:700,fontSize:14,flexShrink:0,width:16,textAlign:"center"}}>{statusIcon[r.status]||"?"}</span>
                  <div style={{flex:1,minWidth:0}}>
                    <span style={{fontSize:11,color:G.muted,marginRight:8}}>Row {r.row}</span>
                    {r.status==="created"&&<><span style={{fontSize:13,color:G.text,fontWeight:600}}>{r.address}</span><span style={{fontSize:12,color:G.accent,marginLeft:8}}>→ {r.client}</span></>}
                    {r.status==="skipped"&&<span style={{fontSize:13,color:G.muted}}>{r.reason}</span>}
                    {r.status==="error"&&<span style={{fontSize:13,color:G.danger}}>{r.reason}</span>}
                  </div>
                  <Badge text={r.status} color={statusColor[r.status]||G.muted} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Root App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState("dashboard");
  const [dashRefresh, setDashRefresh] = useState(0);
  const [initialTicket, setInitialTicket] = useState(null);
  const [booting, setBooting] = useState(true);
  const [contractorApp, setContractorApp] = useState(null);
  const [loadingApp, setLoadingApp] = useState(false);

  const G = getTheme(user, null);

  const appStyles = (
    <>
      <style>{makeCss(G)}</style>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </>
  );

  useEffect(()=>{
    const token = localStorage.getItem("proptrack_token");
    if (token) {
      authAPI.me()
        .then(u => {
          setUser(u);
          const isAlliedRole = ["user","ap_ar","admin","superadmin"].includes(u.role);
          setPage(isAlliedRole ? "properties" : "dashboard");
          if (u.role==="contractor_pending"||u.role==="contractor") {
            setLoadingApp(true);
            applicationsAPI.mine().then(app=>setContractorApp(app)).finally(()=>setLoadingApp(false));
          }
        })
        .catch(()=>localStorage.removeItem("proptrack_token"))
        .finally(()=>setBooting(false));
    } else { setBooting(false); }
  },[]);

  function handleLogin(u) {
    setUser(u); setPage(["user","ap_ar","admin","superadmin"].includes(u.role)?"properties":"dashboard");
    if (u.role==="contractor_pending"||u.role==="contractor") {
      applicationsAPI.mine().then(app=>setContractorApp(app));
    }
  }
  async function handleLogout() {
    try { await authAPI.logout(); } catch {} // best-effort — revoke server-side
    localStorage.removeItem("proptrack_token"); setUser(null); setPage("dashboard"); setContractorApp(null);
  }
  function handleTicketSaved() { setDashRefresh(r=>r+1); setPage("tickets"); }
  function handleProfileUpdated(updatedUser) { setUser(updatedUser); }
  function handleAppSubmitted() { applicationsAPI.mine().then(app=>setContractorApp(app)); }

  const isAdmin = user && (user.role==="admin"||user.role==="superadmin");

  if (booting) return (
    <>
      <style>{makeCss(THEME_ALLIED)}</style>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{display:"flex",alignItems:"center",justifyContent:"center",minHeight:"100vh",background:THEME_ALLIED.bg}}>
        <Spinner G={THEME_ALLIED} />
      </div>
    </>
  );

  if (!user) return <AuthPage onLogin={handleLogin} />;

  if (user.role==="contractor_pending") {
    return (
      <>{appStyles}
        <Nav user={user} page={page} setPage={setPage} onLogout={handleLogout} G={G} />
        {loadingApp ? <div style={{padding:40}}><Spinner G={G} /></div> : (
          <ContractorApplicationPage existingApp={contractorApp} onSubmitted={handleAppSubmitted} G={G} />
        )}
      </>
    );
  }

  const isAllied = ["user","ap_ar","admin","superadmin"].includes(user?.role);

  return (
    <>{appStyles}
      <Nav user={user} page={page} setPage={setPage} onLogout={handleLogout} G={G} />
      {page==="dashboard" && user.role==="contractor" && <ContractorDashboard user={user} G={G} />}
      {page==="dashboard" && isAllied && <PropertiesPage user={user} G={G} defaultView="map" onTicketClick={t=>{setInitialTicket(t);setPage("tickets");}} />}
      {page==="tickets" && isAllied && <TicketsPage user={user} refresh={dashRefresh} onNewTicket={()=>setPage("new-ticket")} initialTicket={initialTicket} clearInitialTicket={()=>setInitialTicket(null)} G={G} />}
      {page==="new-ticket" && (user.role==="user"||isAdmin) && <NewTicketPage onSaved={handleTicketSaved} G={G} />}
      {page==="admin" && isAdmin && <AdminPage currentUser={user} G={G} />}
      {page==="profile" && <ProfilePage user={user} onUpdated={handleProfileUpdated} G={G} />}
      {page==="my-application" && user.role==="contractor" && <ContractorApplicationPage existingApp={contractorApp} onSubmitted={handleAppSubmitted} G={G} />}
      {page==="properties" && isAllied && <PropertiesPage user={user} G={G} onTicketClick={t=>{setInitialTicket(t);setPage("tickets");}} />}
      {page==="clients" && isAllied && <ClientsPage user={user} G={G} />}
    </>
  );
}
